import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import CreateBrowserProfileModal from './CreateBrowserProfileModal';
import ConfigureAuthorizedQaProxyModal from './ConfigureAuthorizedQaProxyModal';
import AuthorizedQaProxyLaunchTokenModal from './AuthorizedQaProxyLaunchTokenModal';
import { useAuth } from '../../../context/AuthContext';
import { copyTextToClipboard, toastCopied, toastCopyFailed } from '../../../utils/fundingClipboardUtils';
import {
  ADAPTIVE_DISPLAY_EXPLANATION,
  archiveBrowserProfile,
  BROWSER_PROFILE_BOOTSTRAP_NOTE,
  buildGoogleQuickSearchUrl,
  CAMOUFOX_OS_OPTIONS,
  canConvertLegacyToPrimary,
  convertBrowserProfileToPrimary,
  createBrowserProfile,
  detectBrowserDeviceInfo,
  downloadBrowserProfileScript,
  enrichBrowserProfileRecord,
  generateCamoufoxProfileScript,
  getBrowserProfileDisplayName,
  getBrowserProfileSetupCopyLabel,
  getBrowserProfileSubtitle,
  getCamoufoxOsLabel,
  getCreateBrowserProfileButtonLabel,
  hasPrimaryBrowserProfile,
  inferTerminalTargetFromOs,
  isAdaptiveDisplayProfile,
  isPrimaryBrowserProfile,
  listBrowserProfiles,
  needsAdaptiveDisplayUpgrade,
  normalizeTerminalTarget,
  POWERSHELL_FROM_CMD_SWITCH_COMMAND,
  resolveBrowserProfileClientId,
  TERMINAL_TARGET_OPTIONS,
  TERMINAL_TARGET_UNIX,
  TERMINAL_TARGET_WINDOWS_CMD,
  TERMINAL_TARGET_WINDOWS_POWERSHELL,
  upgradeBrowserProfileToAdaptive,
} from '../../../services/funding/fundingBrowserProfilesService';
import {
  canManageAuthorizedQaProxy,
  cancelAuthorizedQaProxyTest,
  createConnectionTestRunId,
  disconnectAuthorizedQaProxy,
  isConnectionTestAbortError,
  FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
  fetchBrowserDirectPublicIp,
  formatBytesAllowance,
  formatConnectionTestTimestamp,
  formatLocationSourcePlain,
  formatProxyBadgeLabel,
  formatAuthorizedQaProxyClientSummaryStatus,
  formatAuthorizedQaProxyRoutingStatus,
  formatQaTestLocationSummary,
  formatSuggestionDistanceHint,
  formatUsStateLabel,
  formatWebAccessModeLabel,
  isQaProviderTargetSelectionRequired,
  issueAuthorizedQaProxyLaunchToken,
  getAllowedQaSessionDurations,
  normalizeAuthorizedQaProxySummary,
  normalizeConnectionTestProof,
  PROXY_BADGE_AUTHORIZED,
  PROXY_BADGE_OFF,
  TARGETED_TRAFFIC_COST_NOTICE,
  stripQaSentinelText,
  suspendAuthorizedQaProxy,
  testAuthorizedQaProxy,
  buildAuthorizedQaProxyRunCommand,
  formatQaProxyProviderLabel,
  normalizeQaProxyProviderId,
  QA_PROXY_PROVIDER_911PROXY,
  getAuthorizedQaCurrentSession,
} from '../../../services/funding/fundingAuthorizedQaProxyService';
import {
  acknowledgeQaLauncherCurrent,
  deriveQaBrowserReadiness,
  QA_LAUNCH_BLOCKED_REASON,
  QA_PRIMARY_ACTION,
  QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
  isProofForActiveTarget,
  selectLatestConnectionProof,
} from '../../../services/funding/qaBrowserReadiness';
import {
  QA_EXTERNAL_GEO_STATUS,
  QA_NETWORK_STATUS,
  deriveQaProofStatusModel,
  describeGeoConfidence,
  describeProviderTargeting,
  formatExternalGeoRow,
  formatRequestedTargetPath,
  geoDisagreementHelper,
  sanitizeProviderTargeting,
} from '../../../services/funding/qaConnectionProofModel';

function BrowserProfileField({ label, value, testId, copyTestId, onCopy }) {
  const displayValue = value || '—';
  const canCopy = Boolean(value && value !== '—');

  return (
    <div className="fg-browser-profile-field" data-testid={testId}>
      <div className="fg-browser-profile-field__label">{label}</div>
      <div className="fg-browser-profile-field__row">
        <code className="fg-browser-profile-field__value">{displayValue}</code>
        {canCopy ? (
          <button
            type="button"
            className="fg-browser-profile-field__copy"
            data-testid={copyTestId}
            aria-label={`Copy ${label}`}
            onClick={() => onCopy(value)}
          >
            Copy
          </button>
        ) : null}
      </div>
    </div>
  );
}

function CommandPreview({
  title,
  helper,
  command,
  copyLabel,
  copyTestId,
  codeTestId,
  onCopy,
  warning,
  firstStep,
  scrollable = false,
  defaultExpanded = false,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const rawCommand = typeof command === 'string' ? command : '';

  return (
    <div className="fg-bp-command-preview fg-browser-command-block">
      <div className="fg-browser-command-block__header fg-bp-command-preview__header">
        <div>
          <strong>{title}</strong>
          {helper ? (
            <p
              className="fg-browser-command-block__helper"
              data-testid={`${codeTestId}-helper`}
            >
              {helper}
            </p>
          ) : null}
        </div>
        <div className="fg-bp-command-preview__actions">
          <button
            type="button"
            className="fg-btn fg-btn-light"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            data-testid={codeTestId ? `${codeTestId}-view` : undefined}
          >
            {expanded ? 'Hide' : 'View'}
          </button>
          <button
            type="button"
            className="fg-browser-command-block__copy"
            data-testid={copyTestId}
            data-raw-command={rawCommand}
            onClick={() => onCopy(rawCommand)}
          >
            {copyLabel}
          </button>
        </div>
      </div>
      {warning ? (
        <p className="fg-browser-command-block__warning" data-testid={`${codeTestId}-warning`}>
          {warning}
        </p>
      ) : null}
      {firstStep ? (
        <p className="fg-browser-command-block__first-step" data-testid={`${codeTestId}-first-step`}>
          {firstStep}
        </p>
      ) : null}
      <pre
        className={`fg-browser-command-block__code${scrollable ? ' fg-bp-command-preview__scroll' : ''}`}
        data-testid={codeTestId}
        hidden={!expanded}
      >
        <code>{rawCommand}</code>
      </pre>
    </div>
  );
}

function WindowsTerminalOnboarding({ terminal, onCopy }) {
  if (terminal === TERMINAL_TARGET_WINDOWS_POWERSHELL) {
    return (
      <div
        className="fg-browser-terminal-onboarding"
        data-testid="browser-profile-powershell-onboarding"
      >
        <div
          className="fg-browser-terminal-onboarding__step"
          data-testid="browser-profile-powershell-step1"
        >
          <strong>Step 1 — Switch Command Prompt to PowerShell</strong>
          <p>Open normal Command Prompt and paste:</p>
          <div className="fg-browser-terminal-onboarding__command-row">
            <code data-testid="browser-profile-powershell-noexit-command">
              {POWERSHELL_FROM_CMD_SWITCH_COMMAND}
            </code>
            <button
              type="button"
              className="fg-browser-command-block__copy"
              data-testid="browser-profile-copy-powershell-noexit"
              onClick={() => onCopy(POWERSHELL_FROM_CMD_SWITCH_COMMAND)}
            >
              Copy
            </button>
          </div>
          <p>Press Enter. Continue only when the prompt changes from:</p>
          <code className="fg-browser-terminal-onboarding__prompt" data-testid="browser-profile-cmd-prompt-example">
            C:\Users\...&gt;
          </code>
          <p>to:</p>
          <code className="fg-browser-terminal-onboarding__prompt" data-testid="browser-profile-ps-prompt-example">
            PS C:\Users\...&gt;
          </code>
        </div>
        <div
          className="fg-browser-terminal-onboarding__step"
          data-testid="browser-profile-powershell-step2"
        >
          <strong>Step 2</strong>
          <p>Copy and paste the PowerShell setup command below.</p>
          <p className="fg-field-hint">
            Do not paste the PowerShell setup command into a normal Command Prompt window.
          </p>
        </div>
      </div>
    );
  }

  if (terminal === TERMINAL_TARGET_WINDOWS_CMD) {
    return (
      <div
        className="fg-browser-terminal-onboarding"
        data-testid="browser-profile-cmd-onboarding"
      >
        <div className="fg-browser-terminal-onboarding__step">
          <strong>Windows Command Prompt</strong>
          <p data-testid="browser-profile-cmd-paste-guidance">
            Paste this directly into a normal Command Prompt window.
          </p>
          <p className="fg-field-hint">
            This CMD-safe bootstrap may open PowerShell internally. Do not paste PowerShell syntax into Command Prompt.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

function BrowserProfileGoogleSearchHelper({ profile, clientId, deviceSettings, os, onCopy }) {
  const [query, setQuery] = useState('');
  const searchUrl = useMemo(() => buildGoogleQuickSearchUrl(query), [query]);
  const scriptWithSearch = useMemo(() => {
    if (!searchUrl || !profile) return '';
    return generateCamoufoxProfileScript({
      clientId,
      profileName: profile.profileName,
      startupUrl: searchUrl,
      fallbackWidth: deviceSettings?.fallbackWidth,
      fallbackHeight: deviceSettings?.fallbackHeight,
      fallbackOs: os,
    });
  }, [clientId, deviceSettings, os, profile, searchUrl]);

  return (
    <div className="fg-browser-search-helper" data-testid="browser-profile-google-search">
      <div className="fg-browser-search-helper__header">
        <strong>Google Quick Search</strong>
        <span>Type a query to generate a Google search URL for this profile session.</span>
      </div>
      <label className="fg-browser-search-helper__input-wrap">
        <span className="fg-browser-search-helper__label">Search query</span>
        <input
          type="text"
          className="fg-browser-search-helper__input"
          value={query}
          placeholder="Chase business credit card application"
          data-testid="browser-profile-google-search-input"
          onChange={(event) => setQuery(event.target.value)}
        />
      </label>
      {searchUrl ? (
        <div className="fg-browser-search-helper__result">
          <span className="fg-browser-search-helper__label">Google Quick Search URL</span>
          <div className="fg-browser-profile-field__row">
            <code
              className="fg-browser-profile-field__value"
              data-testid="browser-profile-google-search-url"
            >
              {searchUrl}
            </code>
            <button
              type="button"
              className="fg-browser-command-block__copy"
              data-testid="browser-profile-copy-google-search-url"
              onClick={() => onCopy(searchUrl)}
            >
              Copy Google search URL
            </button>
          </div>
          {scriptWithSearch ? (
            <button
              type="button"
              className="fg-browser-command-block__copy fg-browser-search-helper__script-copy"
              data-testid="browser-profile-copy-script-with-search"
              onClick={() => onCopy(scriptWithSearch)}
            >
              Copy script with this search URL
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MoreMenu({ label = 'More', testId, items = [], disabled = false }) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState('down');
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return undefined;

    const measure = () => {
      const rect = trigger.getBoundingClientRect();
      const menuHeight = Math.max(menu.scrollHeight || 0, menu.offsetHeight || 0, 160);
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const next = (
        spaceBelow < menuHeight + 8 && spaceAbove > spaceBelow
      ) ? 'up' : 'down';
      setPlacement(next);
      const maxSpace = next === 'up' ? spaceAbove - 12 : spaceBelow - 12;
      menu.style.maxHeight = `${Math.max(120, Math.min(menuHeight, maxSpace))}px`;
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <div className="fg-bp-more" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="fg-btn fg-btn-light fg-bp-more__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
        <span aria-hidden="true">{' ···'}</span>
      </button>
      <div
        ref={menuRef}
        className={`fg-bp-more__menu fg-bp-more__menu--${placement}`}
        role="menu"
        hidden={!open}
      >
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            className="fg-bp-more__item"
            data-testid={item.testId}
            disabled={Boolean(item.disabled)}
            title={item.title}
            onClick={() => {
              if (item.disabled) return;
              setOpen(false);
              item.onSelect?.();
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function EnvironmentStrip({
  deviceInfo,
  osOverride,
  terminalTarget,
  onOsOverrideChange,
  onTerminalChange,
}) {
  const effectiveOs = osOverride || deviceInfo?.os;
  const screenLabel = deviceInfo?.screenWidth && deviceInfo?.screenHeight
    ? `${deviceInfo.screenWidth} × ${deviceInfo.screenHeight}`
    : `${deviceInfo?.fallbackWidth || '—'} × ${deviceInfo?.fallbackHeight || '—'}`;

  return (
    <section
      className="fg-bp-environment"
      data-testid="browser-profiles-detected-device"
    >
      <div className="fg-bp-environment__header">
        <h3>Environment</h3>
        <p data-testid="browser-profiles-display-mode-help">{ADAPTIVE_DISPLAY_EXPLANATION}</p>
      </div>
      <div className="fg-bp-env-row">
        <div className="fg-bp-env-cell">
          <span className="fg-bp-env-label">OS</span>
          <strong data-testid="detected-device-os">{getCamoufoxOsLabel(deviceInfo?.os)}</strong>
        </div>
        <div className="fg-bp-env-cell">
          <span className="fg-bp-env-label">Screen</span>
          <strong data-testid="detected-device-screen">{screenLabel}</strong>
        </div>
        <div className="fg-bp-env-cell">
          <span className="fg-bp-env-label">Display</span>
          <strong data-testid="detected-device-mode">Adaptive display</strong>
        </div>
        <label className="fg-bp-env-cell fg-bp-env-cell--control">
          <span className="fg-bp-env-label">Terminal</span>
          <select
            className="fg-application-input"
            value={terminalTarget}
            data-testid="browser-profiles-terminal-selector"
            onChange={(event) => onTerminalChange(event.target.value)}
          >
            {TERMINAL_TARGET_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="fg-bp-env-cell fg-bp-env-cell--control">
          <span className="fg-bp-env-label">Camoufox OS</span>
          <select
            className="fg-application-input"
            value={effectiveOs}
            data-testid="detected-device-os-override"
            onChange={(event) => onOsOverrideChange(event.target.value)}
          >
            {CAMOUFOX_OS_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <span hidden data-testid="detected-device-dpr">{deviceInfo?.devicePixelRatio}</span>
    </section>
  );
}

function NetworkRoutingSummary({ proxy }) {
  const entitled = proxy?.entitled !== false;
  const badge = proxy?.badge || PROXY_BADGE_OFF;
  const routingStatus = formatAuthorizedQaProxyRoutingStatus(proxy);
  const remainingLabel = formatBytesAllowance(proxy?.remainingBytes, proxy?.quotaBytes);
  const lastTest = proxy?.lastConnectionTestStatus
    ? `${proxy.lastConnectionTestStatus}${proxy.lastConnectionTestAt ? ` · ${proxy.lastConnectionTestAt}` : ''}`
    : 'Not tested';
  const fixedStartupUrl = proxy?.fixedStartupUrl || FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL;
  const credentialsReady = Boolean(
    proxy?.providerCredentialsConfigured
    || proxy?.credentialsConfigured
    || proxy?.credentialSaved,
  );
  const statusActive = String(proxy?.status || '').toLowerCase() === 'active'
    || badge === PROXY_BADGE_AUTHORIZED;
  const proxyActive = Boolean(credentialsReady && statusActive);
  const connectionHostPort = proxy?.connectionHostPort
    || (proxy?.host && proxy?.port != null ? `${proxy.host}:${proxy.port}` : null);
  const webAccessLabel = formatWebAccessModeLabel(proxy?.webAccessMode);
  const locationSourceDisplay = formatLocationSourcePlain(
    proxy?.qaLocationSource || proxy?.locationSource,
  );

  return (
    <div className="fg-browser-network-routing" data-testid="browser-profiles-network-routing">
      <div className="fg-browser-network-routing__header">
        <strong>Network routing</strong>
        <p data-testid="browser-profiles-network-routing-help">
          Primary Browser stays direct. Launch a separate Authorized QA Proxy Session when you need
          the approved QA proxy with a geo-aligned environment. That session always opens
          {' '}{FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL}, uses the proxy for all of its traffic, and
          blocks private-network hosts. It does not share Primary Browser cookies or sessions.
        </p>
      </div>

      {!entitled ? (
        <p className="fg-field-hint" data-testid="browser-profiles-proxy-not-entitled">
          Proxy add-on not enabled. Contact your agency administrator.
        </p>
      ) : null}

      <dl className="fg-detected-device-card__grid">
        <div>
          <dt>Primary Browser</dt>
          <dd data-testid="browser-profiles-proxy-primary-direct">Direct</dd>
        </div>
        <div>
          <dt>Authorized QA Proxy Session</dt>
          <dd data-testid="browser-profiles-qa-session-network-status">
            {proxyActive
              ? 'Global proxy + geo-aligned environment'
              : formatProxyBadgeLabel(badge)}
          </dd>
        </div>
        <div>
          <dt>Proxy configuration</dt>
          <dd data-testid="browser-profiles-proxy-routing-status">{routingStatus}</dd>
        </div>
        <div>
          <dt>Provider connection</dt>
          <dd data-testid="browser-profiles-proxy-connection-label">
            {`${formatQaProxyProviderLabel(proxy?.defaultProvider || proxy?.provider)} · Managed by Admin`}
          </dd>
        </div>
        <div>
          <dt>Credentials</dt>
          <dd data-testid="browser-profiles-proxy-credentials">
            {credentialsReady ? 'Configured securely' : 'Not configured'}
          </dd>
        </div>
        {connectionHostPort ? (
          <div>
            <dt>Connection</dt>
            <dd data-testid="browser-profiles-proxy-connection-host">
              {connectionHostPort}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Web access</dt>
          <dd data-testid="browser-profiles-proxy-web-access">{webAccessLabel}</dd>
        </div>
        <div>
          <dt>Policy source</dt>
          <dd data-testid="browser-profiles-proxy-policy-source">
            {proxy?.policySource || 'Admin Control Center'}
          </dd>
        </div>
        <div>
          <dt>QA session startup</dt>
          <dd data-testid="browser-profiles-proxy-fixed-startup">{fixedStartupUrl}</dd>
        </div>
        <div>
          <dt>Geo target</dt>
          <dd data-testid="browser-profiles-qa-test-location">
            {formatQaTestLocationSummary(proxy)}
          </dd>
        </div>
        <div>
          <dt>Location source</dt>
          <dd data-testid="browser-profiles-proxy-location-source">{locationSourceDisplay}</dd>
        </div>
        <div>
          <dt>Private network access</dt>
          <dd data-testid="browser-profiles-proxy-private-network">Blocked</dd>
        </div>
        <div>
          <dt>QA Browser</dt>
          <dd data-testid="browser-profiles-proxy-qa-proxied">Always proxied</dd>
        </div>
        {proxy?.targeting?.usesTargetFilters || proxy?.targeting?.costNotice ? (
          <div className="fg-form-span-2">
            <dt>Targeted traffic</dt>
            <dd data-testid="browser-profiles-qa-targeted-cost-notice">
              {proxy?.targeting?.costNotice || TARGETED_TRAFFIC_COST_NOTICE}
            </dd>
          </div>
        ) : null}
        {entitled ? (
          <div>
            <dt>Remaining traffic allowance</dt>
            <dd data-testid="browser-profiles-proxy-allowance">
              {remainingLabel}
              {proxy?.usageEstimated ? ' (estimated)' : ''}
            </dd>
          </div>
        ) : null}
        {entitled ? (
          <div>
            <dt>Last connection test</dt>
            <dd data-testid="browser-profiles-proxy-last-test">{lastTest}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}

// Re-export for existing tests that import distance hint from this panel.
export { formatSuggestionDistanceHint };

const LAUNCH_BLOCKED_MESSAGES = {
  [QA_LAUNCH_BLOCKED_REASON.NOT_ENTITLED]: 'This client does not have the Authorized QA Proxy add-on enabled.',
  [QA_LAUNCH_BLOCKED_REASON.SUSPENDED]: 'Authorized QA Proxy is suspended for this client. Resume it to launch.',
  [QA_LAUNCH_BLOCKED_REASON.PROVIDER_NOT_CONFIGURED]: 'The QA proxy provider is not configured yet.',
  [QA_LAUNCH_BLOCKED_REASON.TARGET_NOT_READY]: 'Choose a provider-supported QA location before launching.',
  [QA_LAUNCH_BLOCKED_REASON.LAUNCHER_NOT_GENERATED]: 'The QA Browser is not installed yet. Use Install / Update QA Browser.',
  [QA_LAUNCH_BLOCKED_REASON.LAUNCHER_UPDATE_REQUIRED]: 'The QA Browser on this computer is too old to run QA sessions. Use Install / Update QA Browser, then launch.',
  [QA_LAUNCH_BLOCKED_REASON.PROXY_NOT_ACTIVE]: 'Authorized QA Proxy is not active for this client. Activate it in QA Settings.',
};

const PRECISION_LEVELS = {
  zip: ['state', 'city', 'zip'],
  city: ['state', 'city'],
  state: ['state'],
  country: [],
};

function titleCasePlace(value) {
  const text = stripQaSentinelText(String(value || '').trim());
  if (!text) return '';
  return text === text.toLowerCase()
    ? text.replace(/\b\w/g, (ch) => ch.toUpperCase())
    : text;
}

function formatLevelValue(name, value) {
  if (!value) return '';
  if (name === 'state') return formatUsStateLabel(value);
  if (name === 'zip') return `ZIP ${value}`;
  return titleCasePlace(value);
}

/** Verified / not-verified summary from per-level evidence (country, state, city, ZIP). */
function describeLevelVerification(levels, precision) {
  const verified = [];
  const notVerified = [];
  (PRECISION_LEVELS[precision] || []).forEach((name) => {
    const level = levels?.[name];
    if (!level || level.status === 'not_requested') return;
    if (level.status === 'matched') {
      const value = formatLevelValue(name, level.observed || level.requested);
      if (value) verified.push(value);
    } else {
      const value = formatLevelValue(name, level.requested);
      if (value) notVerified.push(value);
    }
  });
  return {
    verified: verified.length ? verified.join(' · ') : null,
    notVerified: notVerified.length ? notVerified.join(' · ') : null,
  };
}

function LocationSelectionSummary({
  requestedLabel,
  reasonLabel,
  disabled,
  onChooseNearby,
  stateOnlyProvider = false,
}) {
  if (stateOnlyProvider) {
    return (
      <div
        className="fg-bp-connection-proof is-failed"
        data-testid="browser-profiles-location-selection-summary"
        data-location-status="state_required"
      >
        <div className="fg-bp-connection-proof__header">
          <strong data-testid="browser-profiles-location-selection-title">QA state required</strong>
        </div>
        <p className="fg-field-hint" data-testid="browser-profiles-location-selection-reason">
          911Proxy requires a valid state target.
        </p>
        <div className="fg-bp-connection-proof__actions">
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profiles-choose-nearby"
            disabled={disabled}
            onClick={onChooseNearby}
          >
            Change QA Location
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className="fg-bp-connection-proof is-failed"
      data-testid="browser-profiles-location-selection-summary"
      data-location-status="selection_required"
    >
      <div className="fg-bp-connection-proof__header">
        <strong data-testid="browser-profiles-location-selection-title">Exact ZIP unavailable</strong>
      </div>
      <dl className="fg-bp-connection-proof__grid">
        <div>
          <dt>Requested location</dt>
          <dd data-testid="browser-profiles-location-selection-requested">
            {stripQaSentinelText(requestedLabel) || '—'}
          </dd>
        </div>
        <div>
          <dt>Provider target</dt>
          <dd data-testid="browser-profiles-location-selection-provider-target">Not selected</dd>
        </div>
      </dl>
      <p className="fg-field-hint" data-testid="browser-profiles-location-selection-reason">
        {stripQaSentinelText(reasonLabel)
          || 'The requested ZIP is not provider-supported. Choose a nearby provider-supported QA location.'}
      </p>
      <p className="fg-field-hint">
        DataImpulse is not contacted until a provider-supported QA location is chosen.
        The client&apos;s Personal Address is not changed.
      </p>
      <div className="fg-bp-connection-proof__actions">
        <button
          type="button"
          className="fg-btn fg-btn-primary"
          data-testid="browser-profiles-choose-nearby"
          disabled={disabled}
          onClick={onChooseNearby}
        >
          Choose nearby QA location
        </button>
      </div>
    </div>
  );
}

function ConnectionProofCard({
  proof,
  testPhase,
  onTestAgain,
  testDisabled,
  canTerminate = false,
  activeLabel,
  activeSource,
  activeTargeting = null,
  proofCurrent = true,
  originalLabel,
  onTerminateTest,
  onChangeLocation,
}) {
  if (testPhase === CONNECTION_TEST_PHASE.RUNNING || testPhase === CONNECTION_TEST_PHASE.STOPPING) {
    const stopping = testPhase === CONNECTION_TEST_PHASE.STOPPING;
    return (
      <div
        className="fg-bp-connection-proof is-testing"
        data-testid="browser-profiles-connection-proof"
        aria-live="polite"
        aria-busy="true"
      >
        <strong data-testid="browser-profiles-connection-testing">
          <span className="fg-bp-connection-proof__spinner" aria-hidden="true" />
          {stopping ? 'Stopping test…' : 'Testing proxy connection…'}
        </strong>
        <p>
          {stopping
            ? 'Waiting for the server to confirm the test stopped. No new result will be saved.'
            : 'Checking provider connection, proxy exit, public IP, requested target, and exit location.'}
        </p>
        {canTerminate ? (
          <div className="fg-bp-connection-proof__actions">
            <button
              type="button"
              className="fg-btn fg-btn-danger"
              data-testid="browser-profiles-proxy-terminate-test"
              disabled={stopping}
              aria-busy={stopping}
              onClick={() => onTerminateTest?.()}
            >
              {stopping ? 'Stopping test…' : 'Terminate Test'}
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const stopped = testPhase === CONNECTION_TEST_PHASE.STOPPED;
  const stoppedNotice = stopped ? (
    <p className="fg-field-hint" data-testid="browser-profiles-connection-test-stopped">
      {proof
        ? 'Connection test stopped. No new result was saved; the last completed result is shown below.'
        : 'No result was saved. The active QA location was not changed.'}
    </p>
  ) : null;
  const actions = (
    <div className="fg-bp-connection-proof__actions">
      <button
        type="button"
        className="fg-btn fg-btn-light"
        data-testid="browser-profiles-connection-proof-retest"
        disabled={testDisabled}
        onClick={onTestAgain}
      >
        {proof || stopped ? 'Test Again' : 'Test Connection'}
      </button>
      {onChangeLocation ? (
        <button
          type="button"
          className="fg-btn fg-btn-light"
          data-testid="browser-profiles-connection-proof-change-location"
          disabled={testDisabled}
          onClick={onChangeLocation}
        >
          Change QA Location
        </button>
      ) : null}
    </div>
  );

  if (!proof) {
    if (!stopped) {
      return (
        <div
          className="fg-bp-connection-proof is-untested"
          data-testid="browser-profiles-connection-proof"
          data-location-status="not_tested"
        >
          <div className="fg-bp-connection-proof__header">
            <strong data-testid="browser-profiles-connection-proof-title">Connection not tested</strong>
          </div>
          <p className="fg-field-hint">
            Optional check: confirms proxy routing and how precisely the exit matches the active
            QA location. Launch QA Session is available either way.
          </p>
          {actions}
        </div>
      );
    }
    return (
      <div
        className="fg-bp-connection-proof is-stopped"
        data-testid="browser-profiles-connection-proof"
        data-location-status="stopped"
      >
        <div className="fg-bp-connection-proof__header">
          <strong data-testid="browser-profiles-connection-proof-title">Connection test stopped</strong>
        </div>
        {stoppedNotice}
        {actions}
      </div>
    );
  }

  // External geo is evidence only: it never changes the requested target or network status.
  const proofTarget = proof.target || {};
  const modelProof = proofTarget.country || proofTarget.zip || proofTarget.state
    || !activeTargeting || !proofCurrent
    ? proof
    : { ...proof, target: activeTargeting };
  const statusModel = deriveQaProofStatusModel(modelProof);
  const networkConfirmed = statusModel.networkStatus === QA_NETWORK_STATUS.CONFIRMED;
  const externalGeoStatus = statusModel.externalGeoStatus;
  const geoConfidence = describeGeoConfidence(modelProof);
  const verification = proof.locationVerification || {};
  const requestedPrecision = String(verification.requestedPrecision || '').toLowerCase();
  const levels = verification.levels && typeof verification.levels === 'object'
    ? verification.levels
    : null;
  const passed = networkConfirmed && externalGeoStatus !== QA_EXTERNAL_GEO_STATUS.MISMATCH;
  const title = networkConfirmed ? 'Proxy connection confirmed' : 'Connection could not be verified';
  const timestamp = formatConnectionTestTimestamp(proof.testedAt);
  const displayActiveLabel = stripQaSentinelText(activeLabel)
    || proof.target?.label
    || formatQaTestLocationSummary({ targeting: proof.target })
    || '—';
  const precisionBreakdown = levels ? describeLevelVerification(levels, requestedPrecision) : null;
  const directSource = proof.directIpSource || 'browser_ipify';
  const reportedGeoRows = (Array.isArray(proof.geoEvidence) ? proof.geoEvidence : [])
    .filter((row) => row && typeof row === 'object');
  const exitGeo = proof.exit || {};
  const geoEvidenceRows = reportedGeoRows.length || !(exitGeo.city || exitGeo.state || exitGeo.postal)
    ? reportedGeoRows
    : [{ source: 'Geo lookup', city: exitGeo.city, state: exitGeo.state, postal: exitGeo.postal }];
  const geoSourcesQueried = Number(verification.geoSourcesQueried) || null;
  const stateVerification = proof.provider === QA_PROXY_PROVIDER_911PROXY ? proof.stateVerification : null;
  const requestedTargetLabel = stateVerification
    ? (stateVerification.requestedStateLabel || formatUsStateLabel(stateVerification.requestedState) || '—')
    : displayActiveLabel;
  const stateMatchLabel = !stateVerification
    ? null
    : stateVerification.status === 'confirmed'
      ? 'Confirmed'
      : stateVerification.status === 'mismatch'
        ? 'Mismatch'
        : 'Not verified';

  return (
    <div
      className={`fg-bp-connection-proof${passed ? ' is-passed' : ' is-failed'}`}
      data-testid="browser-profiles-connection-proof"
      data-location-status={String(proof.locationStatus || '').toLowerCase() || 'unknown'}
      data-network-status={statusModel.networkStatus}
      data-provider-target-status={statusModel.providerTargetStatus}
      data-external-geo-status={externalGeoStatus}
    >
      <div className="fg-bp-connection-proof__header">
        <strong data-testid="browser-profiles-connection-proof-title">{title}</strong>
        {timestamp ? (
          <span data-testid="browser-profiles-connection-proof-time">{timestamp}</span>
        ) : null}
      </div>
      {stoppedNotice}
      <dl className="fg-bp-connection-proof__grid">
        <div>
          <dt>Network</dt>
          <dd data-testid="browser-profiles-connection-proof-network">
            {networkConfirmed
              ? '✓ Proxy confirmed'
              : (statusModel.networkStatus === QA_NETWORK_STATUS.FAILED ? 'Not confirmed' : '—')}
          </dd>
        </div>
        {originalLabel ? (
          <div>
            <dt>Original client location</dt>
            <dd data-testid="browser-profiles-connection-proof-original-location">
              {stripQaSentinelText(originalLabel)}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Requested QA target</dt>
          <dd data-testid="browser-profiles-connection-proof-target">{requestedTargetLabel}</dd>
        </div>
        {activeSource ? (
          <div>
            <dt>Target source</dt>
            <dd data-testid="browser-profiles-connection-proof-target-source">{activeSource}</dd>
          </div>
        ) : null}
        <div>
          <dt>Provider targeting</dt>
          <dd data-testid="browser-profiles-connection-proof-provider-targeting">
            {(() => {
              const targetingText = stateVerification?.providerTargeting
                || describeProviderTargeting(modelProof);
              return proofCurrent ? targetingText : `${targetingText} at test time`;
            })()}
          </dd>
          {proofCurrent ? null : (
            <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-stale">
              The QA location changed after this test. These results are for the previous target;
              use Test Again to check the current one.
            </p>
          )}
        </div>
        {stateVerification ? (
          <div>
            <dt>Observed external state</dt>
            <dd data-testid="browser-profiles-connection-proof-observed-state">
              {stateVerification.observedStateLabel
                || formatUsStateLabel(stateVerification.observedState)
                || 'Not reported'}
            </dd>
          </div>
        ) : null}
        {stateVerification ? (
          <div>
            <dt>State match</dt>
            <dd
              data-testid="browser-profiles-connection-proof-state-match"
              className={stateVerification.status === 'confirmed' ? 'is-success' : 'is-warning'}
            >
              {stateMatchLabel}
            </dd>
          </div>
        ) : null}
        <div>
          <dt data-testid="browser-profiles-connection-proof-exit-label">Last connection test exit</dt>
          <dd data-testid="browser-profiles-connection-proof-exit-ip">
            {proof.proxyExitIp || '—'}
          </dd>
          <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-exit-hint">
            This IP was observed during the connection test. The actual QA Browser may receive a
            different residential exit when launched.
          </p>
        </div>
        <div className="fg-form-span-2">
          <dt>External geo observation</dt>
          <dd data-testid="browser-profiles-connection-proof-geo-evidence">
            {geoEvidenceRows.length ? (
              <ul className="fg-bp-connection-proof__geo-list">
                {geoEvidenceRows.map((row) => (
                  <li key={`${row.source}-${row.postal}-${row.city}`}>{formatExternalGeoRow(row)}</li>
                ))}
              </ul>
            ) : 'No external geo source responded'}
          </dd>
          {geoSourcesQueried && geoSourcesQueried > reportedGeoRows.length ? (
            <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-geo-coverage">
              {`${reportedGeoRows.length} of ${geoSourcesQueried} geo sources responded`}
            </p>
          ) : null}
        </div>
        <div className="fg-form-span-2">
          <dt>Geo confidence</dt>
          <dd
            data-testid="browser-profiles-connection-proof-location-status"
            className={`is-${geoConfidence.tone}`}
          >
            {geoConfidence.label}
          </dd>
          {precisionBreakdown?.verified ? (
            <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-verified">
              {`Confirmed by external geo: ${precisionBreakdown.verified}`}
            </p>
          ) : null}
          {precisionBreakdown?.notVerified ? (
            <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-not-verified">
              {`Not confirmed: ${precisionBreakdown.notVerified}`}
            </p>
          ) : null}
          {proofCurrent && externalGeoStatus !== QA_EXTERNAL_GEO_STATUS.MATCHED ? (
            <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-location-helper">
              {geoDisagreementHelper(modelProof)}
            </p>
          ) : null}
        </div>
        <div>
          <dt>Computer / Direct IP</dt>
          <dd data-testid="browser-profiles-connection-proof-direct-ip">
            {proof.directIp || '—'}
          </dd>
          <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-direct-hint">
            Browser-side probe ({directSource}). Comparison only — does not configure QA Browser.
          </p>
        </div>
        {proof.latencyMs != null ? (
          <div>
            <dt>Latency</dt>
            <dd data-testid="browser-profiles-connection-proof-latency">
              {`${proof.latencyMs} ms`}
            </dd>
          </div>
        ) : null}
      </dl>
      {!networkConfirmed && proof.message ? (
        <p className="fg-field-hint" data-testid="browser-profiles-connection-proof-message">
          {proof.message}
        </p>
      ) : null}

      {actions}
    </div>
  );
}

export const CONNECTION_TEST_PHASE = Object.freeze({
  RUNNING: 'running',
  STOPPING: 'stopping',
  STOPPED: 'stopped',
});

const QA_LAUNCH_TOKEN_TTL_SECONDS = 300;

const CONNECTION_TEST_STOPPED_NOTICE = 'Connection test stopped. No result was saved and the active QA location was not changed.';

function profileStatusLabel(profile) {
  const raw = String(profile?.status || 'ready').trim().toLowerCase();
  if (!raw || raw === 'active' || raw === 'ready') return 'Ready';
  return profile.status || 'Ready';
}

function PrimaryBrowserCard({
  profile,
  clientId,
  convertingProfileId,
  upgradingProfileId,
  onArchive,
  onConvertToPrimary,
  onUpgradeToAdaptive,
  profiles,
  osLabel,
  onOpenPrimary,
  onCopy,
}) {
  const displayName = getBrowserProfileDisplayName(profile) || 'Primary Browser';
  const isPrimary = isPrimaryBrowserProfile(profile);
  const isAdaptive = isAdaptiveDisplayProfile(profile);
  const showAdaptiveUpgrade = needsAdaptiveDisplayUpgrade(profile);
  const showConvertAction = canConvertLegacyToPrimary(profile, profiles);
  const statusLabel = profileStatusLabel(profile);
  const screenLabel = isAdaptive
    ? 'Detected at launch'
    : `${profile.width || '—'} × ${profile.height || '—'}`;
  const startupUrl = profile.startupUrl || '';
  const runAgainCommand = profile.commands?.runAgainCommand || '';

  return (
    <article className="fg-browser-profile-card fg-bp-primary-card" data-testid="browser-profile-card">
      {showConvertAction ? (
        <div className="fg-legacy-profile-banner" data-testid="browser-profile-legacy-warning">
          <p>
            Legacy profile detected. Convert this profile to the standard Primary Browser format.
          </p>
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profile-convert-primary"
            disabled={convertingProfileId === profile.id}
            onClick={() => onConvertToPrimary(profile)}
          >
            {convertingProfileId === profile.id ? 'Converting…' : 'Convert to Primary Browser'}
          </button>
        </div>
      ) : null}

      {showAdaptiveUpgrade ? (
        <div className="fg-legacy-profile-banner" data-testid="browser-profile-adaptive-upgrade">
          <p>
            This saved profile still uses a fixed viewport and zoom. Upgrade to adaptive display
            without deleting cookies, sessions, or the local profile folder.
          </p>
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profile-upgrade-adaptive"
            disabled={upgradingProfileId === profile.id}
            onClick={() => onUpgradeToAdaptive(profile)}
          >
            {upgradingProfileId === profile.id ? 'Upgrading…' : 'Upgrade to adaptive display'}
          </button>
        </div>
      ) : null}

      <div className="fg-bp-primary-card__header">
        <div>
          <div className="fg-profile-title-row">
            <h3 data-testid="browser-profile-display-name">{displayName}</h3>
            {isPrimary ? (
              <span className="fg-badge fg-badge-primary" data-testid="browser-profile-primary-badge">
                Primary
              </span>
            ) : null}
            <span className="fg-badge fg-badge-secure" data-testid="browser-profile-ready-badge">
              {statusLabel}
            </span>
          </div>
          <p data-testid="browser-profile-subtitle">
            Your normal direct browser for this client. Uses this computer&apos;s
            normal network connection. If the computer uses a VPN, Primary Browser
            may use that VPN.
          </p>
          <p className="fg-field-hint" data-testid="browser-profiles-primary-network-hint">
            Direct connection — not the QA proxy.
          </p>
          <p className="fg-field-hint" hidden>
            {getBrowserProfileSubtitle(profile, clientId)}
          </p>
        </div>
      </div>

      <div className="fg-profile-meta fg-bp-primary-card__tags" data-testid="browser-profile-display-badges">
        <span data-testid="browser-profiles-primary-network-status">Direct</span>
        <span>{osLabel}</span>
        <span>{isAdaptive ? 'Display: Adaptive' : 'Display: Fixed'}</span>
        <span>{isAdaptive ? 'Screen: Detected at launch' : `Screen: ${screenLabel}`}</span>
        <span>Theme: Light native controls</span>
        <span>Persistent profile</span>
      </div>

      <div className="fg-bp-primary-card__startup" data-testid="browser-profile-field-startup-url">
        <span className="fg-bp-env-label">Starts at</span>
        <code>{startupUrl || '—'}</code>
      </div>

      <div className="fg-bp-primary-card__reopen" data-testid="browser-profiles-primary-reopen">
        <CommandPreview
          title="Reopen Primary Browser"
          helper="Primary run only — opens the DIRECT browser (fcl_*_primary.py). Never use this for QA."
          command={runAgainCommand}
          copyLabel="Copy Primary Command"
          copyTestId="browser-profile-copy-run-again"
          codeTestId="browser-profile-run-again-command"
          onCopy={onCopy}
          defaultExpanded={false}
        />
      </div>

      <div className="fg-profile-actions fg-bp-primary-card__actions">
        <button
          type="button"
          className="fg-btn fg-btn-primary"
          data-testid="browser-profiles-open-primary"
          onClick={() => onOpenPrimary(profile)}
        >
          Open Primary Browser
        </button>
        <button
          type="button"
          className="fg-btn fg-btn-danger"
          onClick={() => onArchive(profile)}
        >
          Archive
        </button>
      </div>
    </article>
  );
}

const CURRENT_QA_SESSION_POLL_MS = 60 * 1000;

function formatQaSessionTimestamp(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const month = date.toLocaleString('en-US', { month: 'short' });
  const time = date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${month} ${date.getDate()} at ${time}`;
}

const CURRENT_QA_SESSION_STATUS_LABELS = {
  active: 'Active',
  stale: 'Stale',
  closed: 'Closed',
  expired: 'Expired',
};

// Shows only the browser-verified session from IP Usage; the connection test exit never feeds it.
function CurrentQaBrowserSessionCard({ session }) {
  const status = String(session?.status || '').toLowerCase();
  const live = status === 'active' || status === 'stale';
  const ended = status === 'closed' || status === 'expired';
  return (
    <div
      className={`fg-bp-connection-proof${status === 'active' ? ' is-passed' : ''}`}
      data-testid="browser-profiles-current-session"
      data-session-status={status || 'none'}
    >
      <div className="fg-bp-connection-proof__header">
        <strong>Current QA Browser session</strong>
      </div>
      {status === 'expired' ? (
        <div data-testid="browser-profiles-current-session-expired">
          <p><strong>QA Session Expired</strong></p>
          <p className="fg-field-hint">
            Your previous QA session ended because its secure proxy connection expired.
            Starting a new session creates and verifies a fresh connection.
          </p>
        </div>
      ) : null}
      {!live ? (
        <p className="fg-field-hint" data-testid="browser-profiles-current-session-empty">
          No active verified QA Browser session.
        </p>
      ) : null}
      {session && (live || ended) ? (
        <dl className="fg-bp-connection-proof__grid">
          <div>
            <dt>Status</dt>
            <dd data-testid="browser-profiles-current-session-status">
              {CURRENT_QA_SESSION_STATUS_LABELS[status]}
            </dd>
          </div>
          <div>
            <dt>Provider</dt>
            <dd>{formatQaProxyProviderLabel(normalizeQaProxyProviderId(session.provider))}</dd>
          </div>
          {live ? (
            <div>
              <dt>Verified exit IP</dt>
              <dd data-testid="browser-profiles-current-session-exit-ip">{session.exit_ip || '—'}</dd>
            </div>
          ) : null}
          <div>
            <dt>Observed state</dt>
            <dd data-testid="browser-profiles-current-session-state">{session.observed_state || '—'}</dd>
          </div>
          <div>
            <dt>Observed city</dt>
            <dd data-testid="browser-profiles-current-session-city">{session.observed_city || '—'}</dd>
          </div>
          <div>
            <dt>Observed postal</dt>
            <dd data-testid="browser-profiles-current-session-postal">{session.observed_postal || '—'}</dd>
          </div>
          <div>
            <dt>Verified at</dt>
            <dd data-testid="browser-profiles-current-session-verified-at">
              {formatQaSessionTimestamp(session.verified_at)}
            </dd>
          </div>
          <div>
            <dt>{ended ? 'Ended' : 'Last seen'}</dt>
            <dd data-testid="browser-profiles-current-session-last-seen">
              {formatQaSessionTimestamp(ended ? session.closed_at : session.last_seen_at)}
            </dd>
          </div>
        </dl>
      ) : null}
      {status === 'stale' ? (
        <p className="fg-field-hint" data-testid="browser-profiles-current-session-stale">
          The QA Browser has not reported in over 15 minutes.
        </p>
      ) : null}
    </div>
  );
}

function QaBrowserCard({
  profile,
  readiness,
  isProxyManager,
  proxyBusyAction,
  connectionProof,
  connectionTestPhase,
  currentSession = null,
  installPanelRef,
  installPanelRequest = 0,
  onConfigureProxy,
  onTestProxy,
  onTerminateTest,
  onDisconnectProxy,
  onSuspendProxy,
  onIssueProxyLaunchToken,
  onRegenerateProxyLauncher,
  onAcknowledgeInstall,
  onCopy,
}) {
  const proxy = profile.authorizedQaProxy || { badge: PROXY_BADGE_OFF, entitled: true };
  const entitled = proxy?.entitled !== false;
  const providerId = normalizeQaProxyProviderId(proxy?.defaultProvider || proxy?.provider);
  const providerLabel = formatQaProxyProviderLabel(providerId);
  const hasQaProxySession = Boolean(profile.authorizedQaProxySession?.scriptFilename);
  const regenerateCommand = profile.authorizedQaProxySession?.commands?.regenerateCommand || '';
  const locationSummary = formatQaTestLocationSummary(proxy);
  const locationSource = formatLocationSourcePlain(
    proxy?.qaLocationSource || proxy?.locationSource,
  );
  const originalRequested = proxy?.qaOriginalRequestedTargeting
    || proxy?.qaRequestedTargeting
    || proxy?.locationSelectionRequired?.requestedTargeting
    || null;
  const originalSummary = originalRequested
    ? formatQaTestLocationSummary({
      targeting: originalRequested,
      providerTargetReady: false,
      providerTargetStatus: 'selection_required',
      qaRequestedTargeting: originalRequested,
    })
    : null;
  const providerReady = proxy?.providerTargetReady !== false
    && String(proxy?.providerTargetStatus || '').toLowerCase() !== 'selection_required';
  const showOriginalVsActive = Boolean(
    originalSummary
    && (providerReady ? originalSummary !== locationSummary : true)
  ) || !providerReady;
  // Selection workflow only when no active provider target exists; otherwise verification only.
  const selectionRequiredActive = !providerReady || isQaProviderTargetSelectionRequired(proxy);
  const selectionReasonLabel = proxy?.locationSelectionRequired?.fallback?.reasonLabel || null;
  const webAccessLabel = formatWebAccessModeLabel(proxy?.webAccessMode);
  const primaryAction = readiness?.primaryAction || QA_PRIMARY_ACTION.SETUP;
  const lastSessionExpired = providerId === QA_PROXY_PROVIDER_911PROXY
    && String(currentSession?.status || '').toLowerCase() === 'expired';
  const primaryLabel = lastSessionExpired && primaryAction === QA_PRIMARY_ACTION.LAUNCH
    ? 'Start Fresh QA Session'
    : readiness?.primaryActionLabel || 'Set up QA Browser';
  const primaryDisabled = primaryAction === QA_PRIMARY_ACTION.NOT_ENTITLED
    || Boolean(proxyBusyAction)
    || !isProxyManager;
  const [installPanelRequested, setInstallPanelRequested] = useState(false);
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState(null);
  const sessionDurationOptions = primaryAction === QA_PRIMARY_ACTION.LAUNCH
    ? getAllowedQaSessionDurations(providerId, null)
    : [];
  const showInstallPanel = primaryAction === QA_PRIMARY_ACTION.INSTALL_UPDATE
    || (installPanelRequested && hasQaProxySession);
  const openInstallPanel = () => {
    setInstallPanelRequested(true);
    window.setTimeout(() => {
      installPanelRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  };
  const canOfferInstallUpdate = Boolean(isProxyManager && entitled && hasQaProxySession);
  useEffect(() => {
    if (installPanelRequest > 0 && hasQaProxySession) openInstallPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installPanelRequest]);
  const nextStep = readiness?.nextStep || { title: 'Continue setup', body: 'Complete the remaining QA Browser steps.' };
  const stepState = readiness?.steps || {};

  const steps = [
    {
      id: 'provider',
      number: 1,
      title: 'Provider',
      detail: providerLabel,
      done: Boolean(stepState.provider?.done),
      status: stepState.provider?.status || 'Pending',
    },
    {
      id: 'location',
      number: 2,
      title: 'QA Location',
      detail: !providerReady
        ? `Provider-supported location required · Requested ${originalSummary || locationSummary}`
        : showOriginalVsActive
          ? `Active: ${locationSummary} · ${locationSource}. Original/client: ${originalSummary}`
          : `${locationSummary} · ${locationSource}`,
      done: Boolean(stepState.location?.done),
      status: stepState.location?.status || 'Next',
    },
    {
      id: 'connection',
      number: 3,
      title: 'Connection',
      detail: stepState.connection?.detail || 'Not tested',
      done: Boolean(stepState.connection?.done),
      status: stepState.connection?.status || 'Not tested',
    },
    {
      id: 'launcher',
      number: 4,
      title: 'QA Browser',
      detail: stepState.launcher?.detail || 'Install the QA Browser on this computer',
      done: Boolean(stepState.launcher?.done),
      status: stepState.launcher?.status || 'Waiting',
    },
  ];

  const handlePrimary = () => {
    if (primaryDisabled) return;
    switch (primaryAction) {
      case QA_PRIMARY_ACTION.SETUP:
      case QA_PRIMARY_ACTION.SET_LOCATION:
      case QA_PRIMARY_ACTION.CHOOSE_NEARBY:
        onConfigureProxy(profile);
        break;
      case QA_PRIMARY_ACTION.INSTALL_UPDATE:
        installPanelRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
        break;
      case QA_PRIMARY_ACTION.LAUNCH:
        onIssueProxyLaunchToken(profile, sessionDurationMinutes);
        break;
      case QA_PRIMARY_ACTION.SUSPENDED:
        onSuspendProxy(profile);
        break;
      default:
        break;
    }
  };

  const primaryTestId = primaryAction === QA_PRIMARY_ACTION.LAUNCH
    ? 'browser-profiles-proxy-issue-token'
    : primaryAction === QA_PRIMARY_ACTION.INSTALL_UPDATE
      ? 'browser-profiles-proxy-regenerate-hint'
      : primaryAction === QA_PRIMARY_ACTION.SUSPENDED
        ? 'browser-profiles-proxy-suspend'
        : (primaryAction === QA_PRIMARY_ACTION.SETUP
          || primaryAction === QA_PRIMARY_ACTION.SET_LOCATION
          || primaryAction === QA_PRIMARY_ACTION.CHOOSE_NEARBY)
          ? 'browser-profiles-proxy-configure'
          : 'browser-profiles-qa-primary-action';

  // Connection testing lives only in the connection proof card (single Test Again).
  const moreItems = [];
  if (
    primaryAction !== QA_PRIMARY_ACTION.SETUP
    && primaryAction !== QA_PRIMARY_ACTION.SET_LOCATION
    && primaryAction !== QA_PRIMARY_ACTION.CHOOSE_NEARBY
  ) {
    moreItems.push({
      id: 'settings',
      label: 'Change QA Location',
      testId: 'browser-profiles-proxy-configure-more',
      disabled: !isProxyManager || Boolean(proxyBusyAction) || !entitled,
      onSelect: () => onConfigureProxy(profile),
    });
  }
  if (hasQaProxySession && primaryAction !== QA_PRIMARY_ACTION.INSTALL_UPDATE) {
    moreItems.push({
      id: 'install-update',
      label: 'Install / Update QA Browser',
      testId: 'browser-profiles-qa-install-update-more',
      disabled: !canOfferInstallUpdate,
      onSelect: openInstallPanel,
    });
  }
  if (hasQaProxySession) {
    moreItems.push({
      id: 'regenerate',
      label: 'Regenerate launcher',
      testId: 'browser-profiles-proxy-regenerate-hint-more',
      onSelect: () => onRegenerateProxyLauncher(profile),
    });
    moreItems.push({
      id: 'copy-regenerate',
      label: 'Copy Regenerate Command',
      testId: 'browser-profiles-proxy-copy-regenerate',
      onSelect: () => onRegenerateProxyLauncher(profile),
    });
  }
  if (primaryAction !== QA_PRIMARY_ACTION.SUSPENDED) {
    moreItems.push({
      id: 'suspend',
      label: proxy?.status === 'suspended' ? 'Resume proxy' : 'Suspend proxy',
      testId: 'browser-profiles-proxy-suspend',
      disabled: !isProxyManager || Boolean(proxyBusyAction) || !entitled,
      onSelect: () => onSuspendProxy(profile),
    });
  }
  moreItems.push({
    id: 'disconnect',
    label: 'Disconnect proxy',
    testId: 'browser-profiles-proxy-disconnect',
    disabled: !isProxyManager || Boolean(proxyBusyAction) || !entitled,
    onSelect: () => onDisconnectProxy(profile),
  });

  return (
    <article className="fg-bp-qa-card" data-testid="browser-profiles-qa-browser-card">
      <div className="fg-bp-qa-card__header">
        <div>
          <h3>QA Browser</h3>
          <p className="fg-field-hint" data-testid="browser-profiles-qa-provider-label">
            {`Provider: ${providerLabel}`}
          </p>
          <p data-testid="browser-profiles-qa-browser-subtitle">
            Protected QA proxy connection. Always routes through the configured QA
            provider. Never opens Primary Browser.
          </p>
        </div>
        <div data-testid="browser-profiles-client-summary" className="fg-bp-qa-card__badges">
          <span
            className="fg-badge"
            data-testid="browser-profiles-qa-browser-badge"
          >
            {readiness?.sessionLaunchable
              ? 'QA Browser Ready'
              : (readiness?.statusLabel || 'Setup required')}
          </span>
          <span
            className="fg-badge fg-browser-proxy-badge"
            data-testid="browser-profiles-summary-proxy-badge"
          >
            {formatAuthorizedQaProxyClientSummaryStatus(proxy)}
          </span>
        </div>
      </div>

      <div className="fg-bp-qa-card__body">
        <ol className="fg-bp-steps" data-testid="browser-profiles-qa-checklist">
          {steps.map((step) => (
            <li
              key={step.id}
              className={`fg-bp-step${step.done ? ' is-done' : ''}`}
              data-testid={`browser-profiles-qa-check-${step.id}`}
            >
              <span className="fg-bp-step__number" aria-hidden="true">{step.number}</span>
              <div className="fg-bp-step__content">
                <div className="fg-bp-step__title-row">
                  <strong>{step.title}</strong>
                  <span className="fg-bp-step__status">{step.status}</span>
                </div>
                <p>{step.detail}</p>
              </div>
            </li>
          ))}
        </ol>

        <aside className="fg-bp-qa-summary" data-testid="browser-profiles-qa-summary">
          <dl>
            <div>
              <dt>Provider</dt>
              <dd data-testid="browser-profiles-qa-summary-provider">{providerLabel}</dd>
            </div>
            <div>
              <dt>Active QA Location</dt>
              <dd data-testid="browser-profiles-qa-active-location">
                {providerReady ? locationSummary : 'Not selected'}
              </dd>
            </div>
            {showOriginalVsActive || originalSummary ? (
              <div>
                <dt>{providerReady ? 'Original / client location' : 'Requested QA Location'}</dt>
                <dd data-testid="browser-profiles-qa-original-location">
                  {originalSummary || locationSummary}
                </dd>
              </div>
            ) : null}
            <div>
              <dt>Location source</dt>
              <dd data-testid="browser-profiles-qa-location-source-summary">
                {locationSource}
              </dd>
            </div>
            <div>
              <dt>Network</dt>
              <dd data-testid="browser-profiles-proxy-qa-proxied">Always proxied</dd>
            </div>
            <div>
              <dt>Web access</dt>
              <dd data-testid="browser-profiles-proxy-web-access">{webAccessLabel}</dd>
            </div>
            <div>
              <dt>Private network</dt>
              <dd data-testid="browser-profiles-proxy-private-network">Blocked</dd>
            </div>
          </dl>

          <div className="fg-bp-next-step" data-testid="browser-profiles-qa-next-step">
            <span className="fg-bp-env-label">Next step</span>
            <strong>{nextStep.title}</strong>
            <p>{nextStep.body}</p>
          </div>

          <div className="fg-bp-qa-card__actions" data-testid="browser-profiles-proxy-admin-actions">
            {primaryAction === QA_PRIMARY_ACTION.LAUNCH && sessionDurationOptions.length > 1 ? (
              <label className="fg-bp-session-duration" data-testid="browser-profiles-qa-session-duration">
                <span>Maximum Authorized QA Time</span>
                <select
                  className="fg-application-input fg-application-select"
                  value={sessionDurationMinutes ?? ''}
                  onChange={(event) => setSessionDurationMinutes(
                    event.target.value ? Number(event.target.value) : null,
                  )}
                >
                  <option value="">Default</option>
                  {sessionDurationOptions.map((minutes) => (
                    <option key={minutes} value={minutes}>{minutes} min</option>
                  ))}
                </select>
              </label>
            ) : null}
            <button
              type="button"
              className="fg-btn fg-btn-primary"
              data-testid={primaryTestId}
              disabled={primaryDisabled}
              onClick={handlePrimary}
            >
              {primaryLabel}
            </button>
            {readiness?.secondary?.installUpdate && canOfferInstallUpdate ? (
              <button
                type="button"
                className="fg-btn fg-btn-light"
                data-testid="browser-profiles-qa-install-update-secondary"
                disabled={Boolean(proxyBusyAction)}
                onClick={openInstallPanel}
              >
                Install / Update
              </button>
            ) : null}
            {isProxyManager && entitled ? (
              <button
                type="button"
                className="fg-btn fg-btn-light"
                data-testid={
                  primaryAction === QA_PRIMARY_ACTION.SETUP
                  || primaryAction === QA_PRIMARY_ACTION.SET_LOCATION
                  || primaryAction === QA_PRIMARY_ACTION.CHOOSE_NEARBY
                    ? 'browser-profiles-proxy-configure-settings'
                    : 'browser-profiles-proxy-configure'
                }
                disabled={Boolean(proxyBusyAction)}
                onClick={() => onConfigureProxy(profile)}
              >
                QA Settings
              </button>
            ) : null}
            {isProxyManager && entitled ? (
              <MoreMenu
                label="More"
                testId="browser-profiles-qa-more"
                items={moreItems}
                disabled={Boolean(proxyBusyAction)}
              />
            ) : null}
          </div>
        </aside>
      </div>

      {providerId === QA_PROXY_PROVIDER_911PROXY ? (
        <CurrentQaBrowserSessionCard session={currentSession} />
      ) : null}

      {selectionRequiredActive ? (
        <LocationSelectionSummary
          stateOnlyProvider={providerId === QA_PROXY_PROVIDER_911PROXY}
          requestedLabel={originalSummary || locationSummary}
          reasonLabel={selectionReasonLabel}
          disabled={!isProxyManager || Boolean(proxyBusyAction) || !entitled}
          onChooseNearby={() => onConfigureProxy(profile)}
        />
      ) : (
        <>
        <p className="fg-eyebrow" data-testid="browser-profiles-preflight-heading">Preflight connection test</p>
        <ConnectionProofCard
          proof={connectionProof}
          testPhase={connectionTestPhase}
          testDisabled={!isProxyManager || Boolean(proxyBusyAction) || !entitled}
          canTerminate={isProxyManager}
          activeLabel={locationSummary}
          activeSource={locationSource}
          activeTargeting={proxy?.targeting || null}
          proofCurrent={!connectionProof || isProofForActiveTarget(connectionProof, proxy || {})}
          originalLabel={originalSummary && originalSummary !== locationSummary ? originalSummary : null}
          onTestAgain={() => onTestProxy(profile)}
          onTerminateTest={onTerminateTest}
          onChangeLocation={() => onConfigureProxy(profile)}
        />
        </>
      )}

      {showInstallPanel ? (
        <div
          className="fg-bp-install-panel"
          data-testid="browser-profiles-qa-install-modal"
          ref={installPanelRef}
        >
          <CommandPreview
            title="Install / Update QA Browser"
            helper="Writes fcl_*_authorized_qa_proxy_session.py only. It does not open Primary Browser. After setup, use Launch QA Session for a short-lived token command."
            command={regenerateCommand}
            copyLabel="Copy QA Setup Command"
            copyTestId="browser-profiles-qa-install-copy-setup"
            codeTestId="browser-profiles-qa-install-setup-command"
            onCopy={onCopy}
            defaultExpanded={false}
          />
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profiles-qa-install-complete"
            onClick={() => {
              setInstallPanelRequested(false);
              onAcknowledgeInstall();
            }}
            disabled={!regenerateCommand && !hasQaProxySession}
          >
            I&apos;ve completed setup
          </button>
        </div>
      ) : null}
    </article>
  );
}

function BackupProfileCard({
  profile,
  clientId,
  convertingProfileId,
  upgradingProfileId,
  onCopy,
  onArchive,
  onConvertToPrimary,
  onUpgradeToAdaptive,
  profiles,
}) {
  const [expanded, setExpanded] = useState(false);
  const displayName = getBrowserProfileDisplayName(profile);
  const subtitle = getBrowserProfileSubtitle(profile, clientId);
  const showAdaptiveUpgrade = needsAdaptiveDisplayUpgrade(profile);
  const showConvertAction = canConvertLegacyToPrimary(profile, profiles);
  const scriptFilename = profile.scriptFilename || profile.commands?.scriptFilename;

  return (
    <article className="fg-browser-profile-card fg-bp-backup-card" data-testid="browser-profile-backup-card">
      {showConvertAction ? (
        <div className="fg-legacy-profile-banner" data-testid="browser-profile-legacy-warning">
          <p>
            Legacy profile detected. Convert this profile to the standard Primary Browser format.
          </p>
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profile-convert-primary"
            disabled={convertingProfileId === profile.id}
            onClick={() => onConvertToPrimary(profile)}
          >
            {convertingProfileId === profile.id ? 'Converting…' : 'Convert to Primary Browser'}
          </button>
        </div>
      ) : null}

      {showAdaptiveUpgrade ? (
        <div className="fg-legacy-profile-banner" data-testid="browser-profile-adaptive-upgrade">
          <p>
            This saved profile still uses a fixed viewport and zoom. Upgrade to adaptive display
            without deleting cookies, sessions, or the local profile folder.
          </p>
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profile-upgrade-adaptive"
            disabled={upgradingProfileId === profile.id}
            onClick={() => onUpgradeToAdaptive(profile)}
          >
            {upgradingProfileId === profile.id ? 'Upgrading…' : 'Upgrade to adaptive display'}
          </button>
        </div>
      ) : null}

      <div className="fg-browser-profile-main">
        <div className="fg-profile-icon">CF</div>
        <div>
          <div className="fg-profile-title-row">
            <h3 data-testid="browser-profile-display-name">{displayName}</h3>
          </div>
          <p data-testid="browser-profile-subtitle">{subtitle}</p>
          <div className="fg-profile-meta" data-testid="browser-profile-backup-display-badges">
            <span>{`Status: ${profileStatusLabel(profile)}`}</span>
          </div>
        </div>
      </div>

      <div className="fg-profile-actions">
        <button
          type="button"
          className="fg-btn fg-btn-light"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Hide details' : 'Details'}
        </button>
        <button
          type="button"
          className="fg-btn fg-btn-danger"
          onClick={() => onArchive(profile)}
        >
          Archive
        </button>
      </div>

      <div hidden={!expanded}>
        <BrowserProfileField
          label="User Data Folder"
          value={profile.userDataDir}
          testId={`browser-profile-backup-user-data-dir-${profile.id}`}
          copyTestId={`browser-profile-backup-copy-user-data-dir-${profile.id}`}
          onCopy={onCopy}
        />
        <BrowserProfileField
          label="Script Filename"
          value={scriptFilename}
          testId={`browser-profile-backup-script-filename-${profile.id}`}
          copyTestId={`browser-profile-backup-copy-script-filename-${profile.id}`}
          onCopy={onCopy}
        />
      </div>
    </article>
  );
}

const QA_DIAGNOSTIC_ROWS = [
  ['providerReady', 'Provider ready'],
  ['providerTargetReady', 'Provider target ready'],
  ['activeTargetFingerprint', 'Active target fingerprint'],
  ['proofTargetFingerprint', 'Proof target fingerprint'],
  ['proofCurrent', 'Proof current'],
  ['connectionPhase', 'Connection phase'],
  ['connectionStatus', 'Connection status'],
  ['locationStatus', 'Location status'],
  ['setupAcknowledged', 'Setup acknowledged'],
  ['launcherVersion', 'Launcher version'],
  ['launcherCompatible', 'Launcher compatible'],
  ['launchEligible', 'Launch eligible'],
  ['blockedReason', 'Blocked reason'],
  ['primaryAction', 'Primary action'],
];

function formatDiagnosticValue(value) {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  if (value === null || value === undefined || value === '') return '—';
  // Provider "any" sentinels never render in the UI; show them as a wildcard.
  return String(value).replace(/__all_[a-z]+__/g, '*');
}

function QaBrowserDiagnostics({ diagnostics }) {
  return (
    <div className="fg-bp-qa-diagnostics" data-testid="browser-profiles-qa-diagnostics">
      <strong>QA Browser diagnostics</strong>
      <dl>
        {QA_DIAGNOSTIC_ROWS.map(([key, label]) => (
          <div key={key}>
            <dt>{label}</dt>
            <dd data-testid={`browser-profiles-qa-diagnostic-${key}`}>
              {formatDiagnosticValue(diagnostics[key])}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function QaProviderRequestDiagnostics({ proof }) {
  if (!proof) return null;
  const statusModel = deriveQaProofStatusModel(proof);
  const request = proof.providerRequest || null;
  const rows = [
    ['Requested target', formatRequestedTargetPath(proof) || '—', 'requested-target'],
    [
      'Serialized provider target',
      sanitizeProviderTargeting(request?.targeting) || 'Not recorded — run Test Again to capture',
      'serialized-target',
    ],
    ['Connection-test provider session', request?.sessionPresent ? 'present (separate)' : 'absent', 'test-session'],
    ['QA launch provider session', 'separate — created once per Launch QA Session', 'launch-session'],
    ['Proxy routing', statusModel.networkStatus, 'routing'],
    ['External geo status', statusModel.externalGeoStatus, 'external-geo-status'],
    [
      'External geo sources',
      (proof.geoEvidence || []).filter(Boolean).map(formatExternalGeoRow).join(' | ') || '—',
      'external-geo-sources',
    ],
    ['Target mutated by geo', 'NO', 'target-mutated'],
  ];
  return (
    <div className="fg-bp-provider-diagnostics" data-testid="browser-profiles-provider-diagnostics">
      <strong>QA provider request diagnostics</strong>
      <dl className="fg-bp-connection-proof__grid">
        {rows.map(([label, value, id]) => (
          <div key={id}>
            <dt>{label}</dt>
            <dd data-testid={`browser-profiles-provider-diagnostics-${id}`}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function AdvancedDeveloperTools({
  open,
  onToggle,
  profile,
  clientId,
  defaultFolder,
  deviceSettings,
  os,
  onCopy,
  qaDiagnostics,
  qaConnectionProof = null,
}) {
  if (!profile) return null;

  const commands = profile.commands || {};
  const terminal = profile.terminal || TERMINAL_TARGET_UNIX;
  const isPowerShell = terminal === TERMINAL_TARGET_WINDOWS_POWERSHELL;
  const isCmd = terminal === TERMINAL_TARGET_WINDOWS_CMD;
  const setupCopyLabel = getBrowserProfileSetupCopyLabel(terminal);
  const setupWarning = isPowerShell
    ? 'Terminal required: Windows PowerShell — do not paste this into Command Prompt.'
    : isCmd
      ? 'Paste this CMD-safe bootstrap into Command Prompt. It may open PowerShell internally.'
      : null;
  const scriptFilename = profile.scriptFilename || profile.commands?.scriptFilename;
  const proxy = profile.authorizedQaProxy || { badge: PROXY_BADGE_OFF, entitled: true };

  return (
    <section className="fg-bp-advanced" data-testid="browser-profiles-advanced">
      <button
        type="button"
        className="fg-bp-advanced__toggle"
        aria-expanded={open}
        data-testid="browser-profiles-advanced-toggle"
        onClick={onToggle}
      >
        Advanced / Developer Tools
      </button>

      <div className="fg-bp-advanced__body" hidden={!open}>
        <div className="fg-browser-profile-fields">
          <BrowserProfileField
            label="Client ID"
            value={clientId}
            testId="browser-profile-field-client-id"
            copyTestId="browser-profile-copy-client-id"
            onCopy={onCopy}
          />
          <BrowserProfileField
            label="Default Folder"
            value={defaultFolder}
            testId="browser-profile-field-default-folder"
            copyTestId="browser-profile-copy-default-folder"
            onCopy={onCopy}
          />
          <BrowserProfileField
            label="Client ID (anchor)"
            value={clientId}
            testId="browser-profile-client-id"
            copyTestId="browser-profile-anchor-copy-client-id"
            onCopy={onCopy}
          />
          <BrowserProfileField
            label="Default Folder (anchor)"
            value={defaultFolder}
            testId="browser-profile-default-folder"
            copyTestId="browser-profile-anchor-copy-default-folder"
            onCopy={onCopy}
          />
          <BrowserProfileField
            label="User Data Folder"
            value={profile.userDataDir}
            testId="browser-profile-user-data-dir"
            copyTestId="browser-profile-copy-user-data-dir"
            onCopy={onCopy}
          />
          <BrowserProfileField
            label="Script Filename"
            value={scriptFilename}
            testId="browser-profile-script-filename"
            copyTestId="browser-profile-copy-script-filename"
            onCopy={onCopy}
          />
          <BrowserProfileField
            label="Startup URL"
            value={profile.startupUrl}
            testId="browser-profile-field-startup-url-advanced"
            copyTestId="browser-profile-copy-startup-url"
            onCopy={onCopy}
          />
        </div>

        {qaDiagnostics ? <QaBrowserDiagnostics diagnostics={qaDiagnostics} /> : null}
        <QaProviderRequestDiagnostics proof={qaConnectionProof} />

        <WindowsTerminalOnboarding terminal={terminal} onCopy={onCopy} />

        <CommandPreview
          title="Primary first-time setup command"
          helper={`${BROWSER_PROFILE_BOOTSTRAP_NOTE} This writes and may open Primary Browser only (direct).`}
          command={commands.setupCommand}
          copyLabel={setupCopyLabel}
          copyTestId="browser-profile-copy-setup"
          codeTestId="browser-profile-setup-command"
          onCopy={onCopy}
          warning={setupWarning}
        />

        <CommandPreview
          title="Reopen Primary Browser later"
          helper="Primary run only (fcl_*_primary.py). Never use for QA sessions."
          command={commands.runAgainCommand}
          copyLabel="Copy Primary Command"
          copyTestId="browser-profile-copy-run-again-advanced"
          codeTestId="browser-profile-run-again-command-advanced"
          onCopy={onCopy}
        />

        <CommandPreview
          title="Edit script"
          command={commands.editCommand}
          copyLabel="Edit Script"
          copyTestId="browser-profile-copy-edit"
          codeTestId="browser-profile-edit-command"
          onCopy={onCopy}
        />

        <CommandPreview
          title="Regenerate launcher"
          helper="Replace the local Python launcher with the adaptive display script. This does not delete cookies, sessions, or the profile folder."
          command={commands.regenerateCommand}
          copyLabel="Regenerate launcher"
          copyTestId="browser-profile-copy-regenerate"
          codeTestId="browser-profile-regenerate-command"
          onCopy={onCopy}
        />

        <CommandPreview
          title="Generated script"
          helper="Manual Python script if you want to inspect or edit it."
          command={profile.script}
          copyLabel="Copy script"
          copyTestId="browser-profile-copy-script"
          codeTestId="browser-profile-script"
          onCopy={onCopy}
          scrollable
        />

        <div className="fg-browser-profile-download-row">
          <button
            type="button"
            className="fg-browser-command-block__copy"
            data-testid="browser-profile-download-script"
            onClick={() => downloadBrowserProfileScript(profile.script, scriptFilename)}
          >
            Download .py file
          </button>
        </div>

        <BrowserProfileGoogleSearchHelper
          profile={profile}
          clientId={clientId}
          deviceSettings={deviceSettings}
          os={os}
          onCopy={onCopy}
        />

        <NetworkRoutingSummary proxy={proxy} />
      </div>
    </section>
  );
}

export default function BrowserProfilesPanel({ client, agencyId }) {
  const { user } = useAuth();
  const isProxyManager = canManageAuthorizedQaProxy(user);
  const clientId = resolveBrowserProfileClientId(client);
  const resolvedAgencyId = String(
    client?.agencyId || client?.agency_id || agencyId || '',
  ).trim();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [convertingProfileId, setConvertingProfileId] = useState('');
  const [upgradingProfileId, setUpgradingProfileId] = useState('');
  const [error, setError] = useState('');
  const [deviceInfo, setDeviceInfo] = useState(() => detectBrowserDeviceInfo() || {});
  const [osOverride, setOsOverride] = useState('');
  const [terminalOverride, setTerminalOverride] = useState('');
  const [configureProfile, setConfigureProfile] = useState(null);
  const [proxyBusyAction, setProxyBusyAction] = useState('');
  const [proxyNotice, setProxyNotice] = useState('');
  const [launchTokenSession, setLaunchTokenSession] = useState(null);
  const [launchTokenAutoCopySucceeded, setLaunchTokenAutoCopySucceeded] = useState(false);
  const [launchTokenError, setLaunchTokenError] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [launcherAckTick, setLauncherAckTick] = useState(0);
  const [connectionProofByProfile, setConnectionProofByProfile] = useState({});
  // { runId, profileId, phase, recovered } for the one connection test that may be terminated.
  const [connectionTestRun, setConnectionTestRun] = useState(null);
  const [stoppedTestProfileId, setStoppedTestProfileId] = useState('');
  const connectionTestRunRef = useRef(null);
  const finishedTestRunIdsRef = useRef(new Set());
  const loadRequestIdRef = useRef(0);
  const scopedClientRef = useRef(clientId);
  const lastSavedProxyByProfileRef = useRef({});
  const installPanelRef = useRef(null);
  const [installPanelRequest, setInstallPanelRequest] = useState(0);

  const createButtonLabel = getCreateBrowserProfileButtonLabel(profiles);
  const primaryExists = hasPrimaryBrowserProfile(profiles);
  const defaultFolder = clientId ? `profiles/${clientId}/` : '';
  const effectiveOs = osOverride || deviceInfo?.os || 'macos';
  const terminalTarget = normalizeTerminalTarget(terminalOverride)
    || inferTerminalTargetFromOs(effectiveOs)
    || TERMINAL_TARGET_UNIX;
  const displayProfiles = useMemo(
    () => profiles.map((profile) => enrichBrowserProfileRecord(profile, clientId, {
      os: effectiveOs,
      terminal: terminalTarget,
      fallbackWidth: deviceInfo?.fallbackWidth,
      fallbackHeight: deviceInfo?.fallbackHeight,
    })),
    [clientId, deviceInfo?.fallbackHeight, deviceInfo?.fallbackWidth, effectiveOs, profiles, terminalTarget],
  );
  const primaryProfile = useMemo(
    () => displayProfiles.find((profile) => isPrimaryBrowserProfile(profile)) || displayProfiles[0] || null,
    [displayProfiles],
  );
  const backupProfiles = useMemo(
    () => displayProfiles.filter((profile) => primaryProfile && profile.id !== primaryProfile.id),
    [displayProfiles, primaryProfile],
  );
  const summaryProxy = primaryProfile?.authorizedQaProxy || {
    badge: PROXY_BADGE_OFF,
    entitled: true,
  };

  const activeConnectionProof = useMemo(() => {
    if (!primaryProfile) return null;
    return selectLatestConnectionProof(
      connectionProofByProfile[primaryProfile.id] || null,
      primaryProfile.authorizedQaProxy,
    );
  }, [connectionProofByProfile, primaryProfile]);

  const [currentQaSession, setCurrentQaSession] = useState(null);
  const primaryProfileId = primaryProfile?.id || '';
  const primaryProviderId = normalizeQaProxyProviderId(
    primaryProfile?.authorizedQaProxy?.defaultProvider || primaryProfile?.authorizedQaProxy?.provider,
  );
  useEffect(() => {
    setCurrentQaSession(null);
    if (!primaryProfileId || !clientId || !resolvedAgencyId
      || primaryProviderId !== QA_PROXY_PROVIDER_911PROXY) {
      return undefined;
    }
    let cancelled = false;
    const load = () => getAuthorizedQaCurrentSession({
      clientId,
      agencyId: resolvedAgencyId,
      profileId: primaryProfileId,
    })
      .then((session) => { if (!cancelled) setCurrentQaSession(session); })
      .catch(() => {});
    load();
    const timer = setInterval(load, CURRENT_QA_SESSION_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [clientId, resolvedAgencyId, primaryProfileId, primaryProviderId]);

  const primaryTestPhase = (() => {
    if (!primaryProfile) return '';
    if (connectionTestRun?.profileId === primaryProfile.id) return connectionTestRun.phase;
    return stoppedTestProfileId === primaryProfile.id ? CONNECTION_TEST_PHASE.STOPPED : '';
  })();

  // The only derivation of QA Browser state; the card renders it without reinterpretation.
  const qaReadiness = useMemo(() => {
    if (!primaryProfile) return null;
    const proxy = primaryProfile.authorizedQaProxy;
    return deriveQaBrowserReadiness({
      proxy,
      agencyId: resolvedAgencyId,
      clientId,
      profileId: primaryProfile.id,
      hasQaProxySession: Boolean(primaryProfile.authorizedQaProxySession?.scriptFilename),
      templateVersion: QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
      liveProof: activeConnectionProof,
      connectionTestPhase: primaryTestPhase,
      lastTestedLabel: formatConnectionTestTimestamp(
        activeConnectionProof?.testedAt || proxy?.lastConnectionTestAt,
      ) || '',
    });
    // launcherAckTick forces recompute after local acknowledgment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeConnectionProof,
    clientId,
    launcherAckTick,
    primaryProfile,
    primaryTestPhase,
    resolvedAgencyId,
  ]);

  const loadProfiles = useCallback(async () => {
    if (!clientId || !resolvedAgencyId) return;
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    const clientChanged = scopedClientRef.current !== clientId;
    scopedClientRef.current = clientId;
    if (clientChanged) {
      setProfiles([]);
      lastSavedProxyByProfileRef.current = {};
      setConnectionProofByProfile({});
    }
    setLoading(true);
    setError('');
    try {
      const result = await listBrowserProfiles({ clientId, agencyId: resolvedAgencyId });
      if (loadRequestIdRef.current !== requestId) return;
      const nextProfiles = result.profiles ?? [];
      setProfiles(nextProfiles.map((profile) => {
        const fromApi = normalizeAuthorizedQaProxySummary(profile.authorizedQaProxy)
          || profile.authorizedQaProxy
          || null;
        const saved = lastSavedProxyByProfileRef.current[profile.id];
        if (
          saved?.credentialSaved
          && (
            !fromApi?.credentialSaved
            || (!fromApi?.host && saved.host)
            || (!fromApi?.provider && saved.provider)
          )
        ) {
          return {
            ...profile,
            authorizedQaProxy: {
              ...fromApi,
              ...saved,
              credentialSaved: true,
            },
          };
        }
        return {
          ...profile,
          authorizedQaProxy: fromApi,
        };
      }));
    } catch (err) {
      if (loadRequestIdRef.current !== requestId) return;
      setError(err.message || 'Unable to load browser profiles.');
      setProfiles([]);
    } finally {
      if (loadRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [resolvedAgencyId, clientId]);

  useEffect(() => {
    setDeviceInfo(detectBrowserDeviceInfo());
  }, []);

  useEffect(() => {
    setConfigureProfile(null);
    setProxyNotice('');
    loadProfiles();
  }, [loadProfiles]);

  const patchProfileProxy = (profileId, proxySummary) => {
    const normalized = normalizeAuthorizedQaProxySummary(proxySummary) || proxySummary;
    setProfiles((current) => current.map((item) => (
      item.id === profileId
        ? { ...item, authorizedQaProxy: normalized }
        : item
    )));
  };

  const handleCopy = async (value) => {
    try {
      await copyTextToClipboard(value);
      toastCopied();
    } catch {
      toastCopyFailed();
    }
  };

  const handleOpenPrimaryBrowser = async (profile) => {
    const runAgainCommand = profile?.commands?.runAgainCommand;
    if (!runAgainCommand) {
      setProxyNotice('Primary Browser run command is not available yet.');
      return;
    }
    await handleCopy(runAgainCommand);
    setProxyNotice('Primary Browser run command copied.');
  };

  const handleCreate = async (form) => {
    if (!clientId || !resolvedAgencyId) {
      setError('Browser profiles require a saved funding client ID.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const created = await createBrowserProfile({
        clientId,
        agencyId: resolvedAgencyId,
        profileName: form.profileName,
        displayName: form.displayName,
        width: form.fallbackWidth || deviceInfo?.fallbackWidth,
        height: form.fallbackHeight || deviceInfo?.fallbackHeight,
        startupUrl: form.startupUrl,
        searchEngine: form.searchEngine,
        displayMode: form.displayMode,
        detectedOs: form.detectedOs || effectiveOs,
        detectedScreenWidth: form.detectedScreenWidth || deviceInfo?.fallbackWidth,
        detectedScreenHeight: form.detectedScreenHeight || deviceInfo?.fallbackHeight,
        devicePixelRatio: form.devicePixelRatio || deviceInfo?.devicePixelRatio,
        os: effectiveOs,
      });
      setProfiles((current) => [created, ...current]);
      setShowCreate(false);
    } catch (err) {
      setError(err.message || 'Unable to create browser profile.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async (profile) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;
    try {
      await archiveBrowserProfile({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
      });
      setProfiles((current) => current.filter((item) => item.id !== profile.id));
    } catch (err) {
      toastCopyFailed(err.message || 'Unable to archive browser profile.');
    }
  };

  const handleConvertToPrimary = async (profile) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;

    setConvertingProfileId(profile.id);
    setError('');
    try {
      const converted = await convertBrowserProfileToPrimary({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
      });
      setProfiles((current) =>
        current.map((item) => (item.id === profile.id ? converted : item)),
      );
    } catch (err) {
      toastCopyFailed(err.message || 'Unable to convert browser profile.');
    } finally {
      setConvertingProfileId('');
    }
  };

  const handleUpgradeToAdaptive = async (profile) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;

    setUpgradingProfileId(profile.id);
    setError('');
    try {
      const upgraded = await upgradeBrowserProfileToAdaptive({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
      });
      setProfiles((current) =>
        current.map((item) => (item.id === profile.id ? upgraded : item)),
      );
    } catch (err) {
      toastCopyFailed(err.message || 'Unable to upgrade browser profile.');
    } finally {
      setUpgradingProfileId('');
    }
  };

  const publishConnectionTestRun = (run) => {
    setConnectionTestRun(run ? {
      runId: run.runId,
      profileId: run.profileId,
      phase: run.stopping ? CONNECTION_TEST_PHASE.STOPPING : CONNECTION_TEST_PHASE.RUNNING,
      recovered: Boolean(run.recovered),
    } : null);
  };

  const clearConnectionTestRun = (run) => {
    finishedTestRunIdsRef.current.add(run.runId);
    if (connectionTestRunRef.current !== run) return;
    connectionTestRunRef.current = null;
    publishConnectionTestRun(null);
    setProxyBusyAction((current) => (current === 'test' ? '' : current));
  };

  const finishStoppedConnectionTest = (run, message) => {
    clearConnectionTestRun(run);
    // Abort after clearing so the aborted request is recognized as stale and ignored.
    run.controller?.abort();
    setStoppedTestProfileId(run.profileId);
    setProxyNotice(message || CONNECTION_TEST_STOPPED_NOTICE);
    loadProfiles();
  };

  const handleTestProxy = async (profile) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;
    if (connectionTestRunRef.current) return;
    const run = {
      runId: createConnectionTestRunId(),
      profileId: profile.id,
      stopping: false,
      recovered: false,
      controller: typeof AbortController !== 'undefined' ? new AbortController() : null,
    };
    const isCurrent = () => connectionTestRunRef.current === run;
    connectionTestRunRef.current = run;
    publishConnectionTestRun(run);
    setStoppedTestProfileId('');
    setProxyBusyAction('test');
    setProxyNotice('Testing proxy connection…');
    try {
      const clientDirectIp = await fetchBrowserDirectPublicIp();
      if (!isCurrent()) return;
      const result = await testAuthorizedQaProxy({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
        clientDirectIp: clientDirectIp || null,
        runId: run.runId,
        signal: run.controller?.signal,
      });
      // A terminated or superseded run must never overwrite newer proof/readiness.
      if (!isCurrent()) return;
      if (String(result?.status || '').toLowerCase() === 'cancelled') {
        finishStoppedConnectionTest(run, result?.message);
        return;
      }
      const proof = normalizeConnectionTestProof(result);
      if (proof) {
        setConnectionProofByProfile((current) => ({
          ...current,
          [profile.id]: proof,
        }));
      }
      setProxyNotice(result?.message || 'Connection test finished.');
      clearConnectionTestRun(run);
      await loadProfiles();
    } catch (err) {
      if (!isCurrent()) return;
      if (isConnectionTestAbortError(err)) {
        finishStoppedConnectionTest(run);
        return;
      }
      setProxyNotice(err?.message || 'Proxy connection test failed.');
    } finally {
      if (isCurrent()) clearConnectionTestRun(run);
    }
  };

  const handleTerminateTest = async () => {
    const run = connectionTestRunRef.current;
    if (!run || run.stopping || !clientId || !resolvedAgencyId) return;
    run.stopping = true;
    publishConnectionTestRun(run);
    setProxyNotice('Stopping test…');
    try {
      const result = await cancelAuthorizedQaProxyTest({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: run.profileId,
        runId: run.runId,
      });
      if (connectionTestRunRef.current !== run) return;
      if (String(result?.status || '').toLowerCase() === 'completed') {
        setProxyNotice('The connection test finished before it could be stopped. Showing the result.');
        if (run.recovered) {
          clearConnectionTestRun(run);
          await loadProfiles();
        }
        // A local run's own request delivers the legitimate result.
        return;
      }
      finishStoppedConnectionTest(run, result?.message);
    } catch (err) {
      if (connectionTestRunRef.current !== run) return;
      run.stopping = false;
      publishConnectionTestRun(run);
      setProxyNotice(err?.message || 'Unable to stop the connection test.');
    }
  };

  const serverActiveTest = primaryProfile?.authorizedQaProxy?.activeConnectionTest || null;
  const serverActiveTestRunId = serverActiveTest?.runId || '';

  // Recover a test still running on the server (e.g. after navigating away and back).
  useEffect(() => {
    if (!primaryProfile?.id || !serverActiveTestRunId) return undefined;
    const current = connectionTestRunRef.current;
    if (current?.recovered) {
      if (current.runId !== serverActiveTestRunId) return undefined;
      const timer = setTimeout(() => { loadProfiles(); }, 3000);
      return () => clearTimeout(timer);
    }
    if (current || finishedTestRunIdsRef.current.has(serverActiveTestRunId)) return undefined;
    const run = {
      runId: serverActiveTestRunId,
      profileId: primaryProfile.id,
      stopping: false,
      recovered: true,
      controller: null,
    };
    connectionTestRunRef.current = run;
    publishConnectionTestRun(run);
    setProxyBusyAction('test');
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryProfile?.id, serverActiveTestRunId, serverActiveTest, loadProfiles]);

  // A recovered run ends once the server no longer reports it as running.
  useEffect(() => {
    const current = connectionTestRunRef.current;
    if (!current?.recovered || current.stopping) return;
    if (serverActiveTestRunId === current.runId) return;
    clearConnectionTestRun(current);
    setProxyNotice('Connection test finished.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverActiveTestRunId, connectionTestRun]);

  // Leaving the client or page drops local tracking; the server run is recovered on return.
  useEffect(() => () => {
    const run = connectionTestRunRef.current;
    connectionTestRunRef.current = null;
    run?.controller?.abort();
    setConnectionTestRun(null);
    setStoppedTestProfileId('');
    setProxyBusyAction((current) => (current === 'test' ? '' : current));
  }, [clientId]);

  const handleDisconnectProxy = async (profile) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;
    if (!window.confirm(
      'Disconnect Authorized QA Proxy for this profile? Profile folders, cookies, sessions, and the launcher file will be kept.',
    )) {
      return;
    }
    setProxyBusyAction('disconnect');
    setProxyNotice('');
    try {
      const result = await disconnectAuthorizedQaProxy({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
      });
      patchProfileProxy(profile.id, {
        ...(profile.authorizedQaProxy || {}),
        status: 'off',
        badge: PROXY_BADGE_OFF,
        credentialSaved: false,
        entitled: true,
      });
      setProxyNotice(result.message || 'Proxy disconnected. Profile data was kept.');
      await loadProfiles();
    } catch (err) {
      setProxyNotice(err.message || 'Unable to disconnect proxy.');
    } finally {
      setProxyBusyAction('');
    }
  };

  const handleSuspendProxy = async (profile) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;
    const suspend = profile.authorizedQaProxy?.status !== 'suspended';
    if (suspend && !window.confirm('Suspend Authorized QA Proxy for this profile?')) {
      return;
    }
    setProxyBusyAction('suspend');
    setProxyNotice('');
    try {
      const result = await suspendAuthorizedQaProxy({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
        suspend,
      });
      patchProfileProxy(profile.id, result);
      setProxyNotice(suspend ? 'Proxy suspended.' : 'Proxy resumed.');
    } catch (err) {
      setProxyNotice(err.message || 'Unable to update proxy status.');
    } finally {
      setProxyBusyAction('');
    }
  };

  const clearLaunchTokenSession = useCallback(() => {
    setLaunchTokenSession(null);
    setLaunchTokenAutoCopySucceeded(false);
    setLaunchTokenError('');
  }, []);

  const expireLaunchTokenSession = useCallback(() => {
    setLaunchTokenSession((current) => (
      current ? { ...current, token: '', runCommand: '', expired: true } : current
    ));
    setLaunchTokenAutoCopySucceeded(false);
  }, []);

  const launchVerificationSummary = (profile) => {
    if (primaryProfile?.id !== profile?.id || !qaReadiness) return '';
    if (qaReadiness.connection.locationVerified) return 'Verified';
    return qaReadiness.steps.connection.status || 'Not verified';
  };

  // Launch QA Session never disappears; when a launch cannot start, the reason is shown in
  // the launch modal after the click.
  const openLaunchBlocked = (profile, message, { updateRequired = false } = {}) => {
    const proxy = normalizeAuthorizedQaProxySummary(profile.authorizedQaProxy);
    setLaunchTokenAutoCopySucceeded(false);
    setLaunchTokenError('');
    setLaunchTokenSession({
      blocked: true,
      message,
      updateRequired,
      profileId: profile.id,
      locationSummary: formatQaTestLocationSummary(proxy),
      verificationSummary: launchVerificationSummary(profile),
    });
    setProxyNotice(message);
  };

  const handleIssueProxyLaunchToken = async (profile, sessionDurationMinutes = null) => {
    if (!clientId || !resolvedAgencyId || !profile?.id) return;
    if (primaryProfile?.id === profile.id && qaReadiness && !qaReadiness.launch.eligible) {
      const reason = qaReadiness.launch.blockedReason;
      openLaunchBlocked(
        profile,
        LAUNCH_BLOCKED_MESSAGES[reason] || 'QA Session can\u2019t start yet.',
        { updateRequired: reason === QA_LAUNCH_BLOCKED_REASON.LAUNCHER_UPDATE_REQUIRED },
      );
      return;
    }
    const session = profile.authorizedQaProxySession;
    if (!session?.scriptFilename) {
      openLaunchBlocked(
        profile,
        'The QA Browser launcher has not been generated. Use Install / Update QA Browser.',
        { updateRequired: true },
      );
      return;
    }
    if (!String(session.scriptFilename).includes('authorized_qa_proxy_session')) {
      openLaunchBlocked(
        profile,
        'QA session launcher is missing or points at Primary Browser. '
        + 'Run Install / Update QA Browser, then Launch QA Session again.',
        { updateRequired: true },
      );
      return;
    }
    const proxy = normalizeAuthorizedQaProxySummary(profile.authorizedQaProxy);
    if (
      !(
        proxy?.providerCredentialsConfigured
        || proxy?.credentialsConfigured
        || proxy?.credentialSaved
      )
      || proxy.status === 'suspended'
    ) {
      openLaunchBlocked(profile, 'Configure and save Authorized QA Proxy before launching a QA session.');
      return;
    }
    setProxyBusyAction('token');
    setProxyNotice('');
    setLaunchTokenError('');
    try {
      const issued = await issueAuthorizedQaProxyLaunchToken({
        clientId,
        agencyId: resolvedAgencyId,
        profileId: profile.id,
        startupUrl: FIXED_AUTHORIZED_QA_PROXY_STARTUP_URL,
        ttlSeconds: QA_LAUNCH_TOKEN_TTL_SECONDS,
        sessionDurationMinutes,
      });
      const terminal = profile.terminal || terminalTarget;
      const scriptFilename = session.scriptFilename;
      const compareDirectIp = (
        activeConnectionProof?.directIp
        || proxy?.lastConnectionTestProof?.directIp
        || null
      );
      const runCommand = buildAuthorizedQaProxyRunCommand({
        token: issued.token,
        scriptFilename,
        terminal,
        clientDirectIp: compareDirectIp,
      });
      if (!runCommand || String(runCommand).includes('_primary.py')) {
        setProxyNotice(
          'Refused to build a Primary Browser launch command for QA. '
          + 'Regenerate the QA Browser launcher and try again.',
        );
        return;
      }
      const launchSession = {
        token: issued.token,
        runCommand,
        expiresAt: issued.expiresAt,
        issuedAt: Date.now(),
        terminal,
        scriptFilename,
        profileId: profile.id,
        tokenId: issued.tokenId,
        userDataDir: session.userDataDir,
        locationSummary: formatQaTestLocationSummary(proxy),
        verificationSummary: launchVerificationSummary(profile),
        networkSummary: `${proxy?.providerLabel || formatQaProxyProviderLabel(proxy?.defaultProvider)} · Always proxied`,
      };
      let autoCopySucceeded = false;
      try {
        await copyTextToClipboard(runCommand);
        autoCopySucceeded = true;
      } catch {
        autoCopySucceeded = false;
      }
      setLaunchTokenAutoCopySucceeded(autoCopySucceeded);
      setLaunchTokenSession(launchSession);
      setProxyNotice(
        autoCopySucceeded
          ? 'QA Proxy Session run command copied.'
          : 'Copy the QA Proxy Session command below.',
      );
    } catch (err) {
      const message = err.message || 'Unable to launch Authorized QA Proxy Session.';
      if (launchTokenSession?.profileId === profile.id && !launchTokenSession?.blocked) {
        setLaunchTokenError(message);
        setProxyNotice(message);
      } else {
        openLaunchBlocked(profile, message);
      }
    } finally {
      setProxyBusyAction('');
    }
  };

  const handleRegenerateProxyLauncher = async (profile) => {
    const regenerateCommand = profile?.authorizedQaProxySession?.commands?.regenerateCommand;
    if (!regenerateCommand) {
      setProxyNotice('Authorized QA Proxy Session launcher is not available for this profile.');
      return;
    }
    await handleCopy(regenerateCommand);
    setProxyNotice(
      'QA Proxy Session regenerate command copied. It replaces the QA session launcher without changing the Primary Browser profile.',
    );
  };

  const handleCompleteQaInstall = () => {
    const target = primaryProfile;
    if (!target?.id) return;
    const acknowledgment = acknowledgeQaLauncherCurrent({
      agencyId: resolvedAgencyId,
      clientId,
      profileId: target.id,
      templateVersion: QA_BROWSER_LAUNCHER_TEMPLATE_VERSION,
    });
    setLauncherAckTick((n) => n + 1);
    setProxyNotice(
      acknowledgment.persisted
        ? 'QA Browser setup confirmed for this browser.'
        : 'QA Browser setup confirmed for this page session only. This browser blocked local storage, so FundElite will ask again after you close the tab.',
    );
  };

  if (!clientId) {
    return (
      <section className="fg-browser-profiles-panel" data-testid="browser-profiles-panel">
        <div className="fg-section-header">
          <div>
            <h2>Browser Profiles</h2>
            <p>
              Create secure Camoufox browser profiles linked to this funding client.
              Use the client ID as the profile anchor.
            </p>
          </div>
        </div>
        <p className="fg-form-error" data-testid="browser-profiles-missing-id-warning">
          Browser profiles need a saved client ID. Refresh or reopen this client.
        </p>
      </section>
    );
  }

  return (
    <section className="fg-browser-profiles-panel" data-testid="browser-profiles-panel">
      <div className="fg-section-header">
        <div>
          <h2>Browser Profiles</h2>
          <p>
            Create secure Camoufox browser profiles linked to this funding client.
            Use the client ID as the profile anchor.
          </p>
        </div>

        <button
          type="button"
          className="fg-btn fg-btn-primary"
          data-testid="browser-profile-create-open"
          onClick={() => setShowCreate(true)}
        >
          {`+ ${createButtonLabel}`}
        </button>
      </div>

      <EnvironmentStrip
        deviceInfo={deviceInfo}
        osOverride={osOverride}
        terminalTarget={terminalTarget}
        onOsOverrideChange={setOsOverride}
        onTerminalChange={setTerminalOverride}
      />

      {proxyNotice ? (
        <p className="fg-field-hint" data-testid="browser-profiles-proxy-notice">{proxyNotice}</p>
      ) : null}

      {error ? <p className="fg-form-error">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-[var(--fg-muted)]">Loading browser profiles…</p>
      ) : null}

      {!loading && profiles.length === 0 ? (
        <div className="fg-empty-browser-profile">
          <div className="fg-empty-icon">CF</div>
          <h3>No browser profiles yet</h3>
          <p>
            Create the primary Camoufox profile for this client to keep browser sessions,
            cookies, display, and login state separated.
          </p>
          <button
            type="button"
            className="fg-btn fg-btn-primary"
            data-testid="browser-profile-create-empty"
            onClick={() => setShowCreate(true)}
          >
            Create Primary Browser
          </button>
        </div>
      ) : null}

      {primaryProfile ? (
        <div className="fg-bp-stack">
          <PrimaryBrowserCard
            profile={primaryProfile}
            clientId={clientId}
            convertingProfileId={convertingProfileId}
            upgradingProfileId={upgradingProfileId}
            profiles={displayProfiles}
            osLabel={getCamoufoxOsLabel(effectiveOs)}
            onArchive={handleArchive}
            onConvertToPrimary={handleConvertToPrimary}
            onUpgradeToAdaptive={handleUpgradeToAdaptive}
            onOpenPrimary={handleOpenPrimaryBrowser}
            onCopy={handleCopy}
          />
          <QaBrowserCard
            profile={primaryProfile}
            readiness={qaReadiness}
            isProxyManager={isProxyManager}
            proxyBusyAction={proxyBusyAction}
            connectionProof={activeConnectionProof}
            currentSession={currentQaSession}
            connectionTestPhase={primaryTestPhase}
            onTerminateTest={handleTerminateTest}
            installPanelRef={installPanelRef}
            installPanelRequest={installPanelRequest}
            onConfigureProxy={setConfigureProfile}
            onTestProxy={handleTestProxy}
            onDisconnectProxy={handleDisconnectProxy}
            onSuspendProxy={handleSuspendProxy}
            onIssueProxyLaunchToken={handleIssueProxyLaunchToken}
            onRegenerateProxyLauncher={handleRegenerateProxyLauncher}
            onAcknowledgeInstall={handleCompleteQaInstall}
            onCopy={handleCopy}
          />
        </div>
      ) : null}

      {backupProfiles.length > 0 ? (
        <div className="fg-browser-profile-list fg-bp-backup-list">
          {backupProfiles.map((profile) => (
            <BackupProfileCard
              key={profile.id}
              profile={profile}
              clientId={clientId}
              convertingProfileId={convertingProfileId}
              upgradingProfileId={upgradingProfileId}
              profiles={displayProfiles}
              onCopy={handleCopy}
              onArchive={handleArchive}
              onConvertToPrimary={handleConvertToPrimary}
              onUpgradeToAdaptive={handleUpgradeToAdaptive}
            />
          ))}
        </div>
      ) : null}

      {!primaryProfile && displayProfiles.length > 0 ? (
        <div className="fg-browser-profile-list">
          {displayProfiles.map((profile) => (
            <BackupProfileCard
              key={profile.id}
              profile={profile}
              clientId={clientId}
              convertingProfileId={convertingProfileId}
              upgradingProfileId={upgradingProfileId}
              profiles={displayProfiles}
              onCopy={handleCopy}
              onArchive={handleArchive}
              onConvertToPrimary={handleConvertToPrimary}
              onUpgradeToAdaptive={handleUpgradeToAdaptive}
            />
          ))}
        </div>
      ) : null}

      <AdvancedDeveloperTools
        open={advancedOpen}
        onToggle={() => setAdvancedOpen((value) => !value)}
        profile={primaryProfile || displayProfiles[0] || null}
        clientId={clientId}
        defaultFolder={defaultFolder}
        deviceSettings={deviceInfo}
        os={effectiveOs}
        onCopy={handleCopy}
        qaDiagnostics={qaReadiness?.diagnostics || null}
        qaConnectionProof={activeConnectionProof}
      />

      <CreateBrowserProfileModal
        open={showCreate}
        clientId={clientId}
        hasPrimaryProfile={primaryExists}
        deviceSettings={deviceInfo}
        os={effectiveOs}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
        submitting={submitting}
      />

      <ConfigureAuthorizedQaProxyModal
        open={Boolean(configureProfile)}
        clientId={clientId}
        agencyId={resolvedAgencyId}
        profile={configureProfile}
        proxySummary={configureProfile?.authorizedQaProxy}
        onClose={() => setConfigureProfile(null)}
        onSaved={async (saved) => {
          if (configureProfile?.id) {
            const normalized = normalizeAuthorizedQaProxySummary(saved) || saved;
            lastSavedProxyByProfileRef.current[configureProfile.id] = normalized;
            patchProfileProxy(configureProfile.id, normalized);
          }
          setProxyNotice('Authorized QA Proxy saved. Provider credentials stay managed by Admin.');
          await loadProfiles();
        }}
      />

      <AuthorizedQaProxyLaunchTokenModal
        open={Boolean(launchTokenSession)}
        session={launchTokenSession}
        autoCopySucceeded={launchTokenAutoCopySucceeded}
        generating={proxyBusyAction === 'token'}
        error={launchTokenError}
        onExpire={expireLaunchTokenSession}
        onGenerateNewToken={() => {
          const target = displayProfiles.find((p) => p.id === launchTokenSession?.profileId);
          if (target) handleIssueProxyLaunchToken(target);
        }}
        onRunConnectionTest={() => {
          const target = displayProfiles.find((p) => p.id === launchTokenSession?.profileId);
          clearLaunchTokenSession();
          if (target) handleTestProxy(target);
        }}
        onChangeLocation={() => {
          const target = displayProfiles.find((p) => p.id === launchTokenSession?.profileId);
          clearLaunchTokenSession();
          if (target) setConfigureProfile(target);
        }}
        onInstallUpdate={() => {
          clearLaunchTokenSession();
          setInstallPanelRequest((n) => n + 1);
        }}
        onClose={() => {
          const wasBlocked = Boolean(launchTokenSession?.blocked);
          clearLaunchTokenSession();
          if (!wasBlocked) setProxyNotice('Authorized QA Proxy launch token cleared from this session.');
        }}
      />

      {/* Keep summary proxy reference available for tests that inspect routing copy */}
      <span hidden data-testid="browser-profiles-qa-status-summary">
        {formatAuthorizedQaProxyClientSummaryStatus(summaryProxy)}
      </span>
    </section>
  );
}
